import React, { useState, useEffect, Suspense } from 'react';
import IASideBar from './IASideBar';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import { useParams, useNavigate } from 'react-router-dom';
import auditService from '../../../services/AuditService';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import * as Icons from '@mui/icons-material';
import { Chart } from "react-google-charts";
import { enqueueSnackbar } from 'notistack';
import roleService from '../../../services/RoleService';
import { format, getWeek, getMonth, getYear, startOfMonth, endOfMonth, eachWeekOfInterval } from 'date-fns';
// Add this import for Excel export
import * as XLSX from 'xlsx';
// Explicitly import additional icons needed
import { 
  CalendarMonth, 
  ChevronLeft, 
  ChevronRight, 
  Today, 
  ViewModule, 
  ViewList, 
  LabelImportant,
  Person,
  CalendarToday,
  EventAvailable as EventAvailableIcon,
  EventBusy,
  EditCalendar,
  AutoAwesome,
  Folder,
  Assignment,
  CheckCircle,
  Info,
  DragIndicator,
  AddCircleOutline,
  DeleteOutline,
  Edit,
  ContentCopy,
  NoteAdd,
  AddBox,
  PlaylistAdd,
  Add,
  DeleteSweep,
  DeleteForever,
  WarningAmber,
  FileDownload,
  Description,
  ArrowBack,
  ViewCompact,
  ZoomOut,
  ZoomIn,
  ZoomInMap
} from '@mui/icons-material';

// Activity status colors for both the chart and cards
const statusColors = {
  "Not Started": { bg: '#f8fafc', color: '#475569', barColor: '#94a3b8', icon: <Icons.PendingOutlined /> },
  "In Progress": { bg: '#f0f9ff', color: '#0284c7', barColor: '#0ea5e9', icon: <Icons.RunCircleOutlined /> },
  "Completed": { bg: '#f0fdf4', color: '#16a34a', barColor: '#22c55e', icon: <Icons.CheckCircleOutlined /> },
  "Past Due": { bg: '#fef2f2', color: '#b91c1c', barColor: '#ef4444', icon: <Icons.WarningAmberOutlined /> }
};

// Defines color for Gantt chart items
const getChartStatusColor = (status) => {
  return statusColors[status]?.barColor || '#94a3b8';
};

// Helper function to calculate the week number within a month (W1, W2, W3, W4)
const getWeekOfMonth = (date) => {
  const startOfMonthDate = startOfMonth(date);
  const weeksInMonth = eachWeekOfInterval({
    start: startOfMonthDate,
    end: endOfMonth(date)
  });
  
  // Find which week contains our date
  const dateTime = date.getTime();
  for (let i = 0; i < weeksInMonth.length; i++) {
    const weekStart = weeksInMonth[i];
    const weekEnd = i < weeksInMonth.length - 1 
      ? new Date(weeksInMonth[i + 1].getTime() - 1) 
      : endOfMonth(date);
    
    if (dateTime >= weekStart.getTime() && dateTime <= weekEnd.getTime()) {
      return `W${i + 1}`;
    }
  }
  
  return 'W?';
};

// Format date for Gantt chart display
const formatGanttDate = (date) => {
  const monthYear = format(date, 'MMM yyyy');
  const weekOfMonth = getWeekOfMonth(date);
  return `${monthYear} ${weekOfMonth}`;
};

// Get months for timeline (3 or 6 months, starting from specified date)
const getTimelineMonths = (startDate = new Date(), extendedView = false) => {
  const months = [];
  const currentDate = new Date(startDate);
  
  // Start from current month
  currentDate.setDate(1);
  
  // Add current month and next months (3 or 6 depending on view mode)
  const monthsToShow = extendedView ? 6 : 3;
  
  for (let i = 0; i < monthsToShow; i++) {
    months.push({
      month: format(currentDate, 'MMMM'),
      year: format(currentDate, 'yyyy'),
      date: new Date(currentDate),
      weeks: ['W1', 'W2', 'W3', 'W4']
    });
    
    // Move to next month
    currentDate.setMonth(currentDate.getMonth() + 1);
  }
  
  return months;
};

// Check if activity falls within a specific week of month
const isActivityInWeek = (activity, monthIndex, weekIndex, timelineMonths) => {
  if (!activity.START_DATE || !activity.END_DATE) return false;
  
  const startDate = new Date(activity.START_DATE);
  const endDate = new Date(activity.END_DATE);
  const month = timelineMonths[monthIndex];
  
  // Calculate week dates for this month
  const monthStart = new Date(month.date);
  const monthEnd = new Date(month.date);
  monthEnd.setMonth(monthEnd.getMonth() + 1);
  monthEnd.setDate(0); // Last day of month
  
  const weeksInMonth = [];
  let currentWeekStart = new Date(monthStart);
  
  // Generate week date ranges
  while (currentWeekStart.getMonth() === monthStart.getMonth()) {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    
    if (weekEnd.getMonth() !== monthStart.getMonth()) {
      // If week crosses to next month, use month end as week end
      weeksInMonth.push({
        start: new Date(currentWeekStart),
        end: new Date(monthEnd)
      });
    } else {
      weeksInMonth.push({
        start: new Date(currentWeekStart),
        end: new Date(weekEnd)
      });
    }
    
    // Move to next week
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }
  
  // Use the weeks we have (might be less than 4)
  const weekCount = Math.min(weeksInMonth.length, 4);
  if (weekIndex >= weekCount) return false;
  
  // For simplicity, if there are fewer than 4 weeks, we'll distribute them evenly
  // This is an approximation
  const weekRange = weeksInMonth[weekIndex] || 
    (weekIndex === 3 ? { start: new Date(monthEnd.getTime() - 7*24*60*60*1000), end: monthEnd } : null);
  
  if (!weekRange) return false;
  
  // Check if activity overlaps with this week
  return (
    (startDate <= weekRange.end && endDate >= weekRange.start) ||
    (startDate >= weekRange.start && startDate <= weekRange.end) ||
    (endDate >= weekRange.start && endDate <= weekRange.end)
  );
};

// Check if an activity is past due (end date has passed but activity not completed)
const isPastDue = (activity) => {
  if (!activity || activity.STATUS === 'Completed' || !activity.END_DATE) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const endDate = new Date(activity.END_DATE);
  endDate.setHours(0, 0, 0, 0);
  
  return endDate < today;
};

// After the isActivityInWeek function, add these two new functions
const isFirstWeekOfActivity = (activity, monthIndex, weekIndex, timelineMonths) => {
  if (!activity.START_DATE || !activity.END_DATE) return false;
  
  const startDate = new Date(activity.START_DATE);
  const month = timelineMonths[monthIndex];
  
  // Calculate week dates for this month
  const monthStart = new Date(month.date);
  const weeksInMonth = [];
  let currentWeekStart = new Date(monthStart);
  
  // Generate week date ranges
  while (currentWeekStart.getMonth() === monthStart.getMonth()) {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    
    weeksInMonth.push({
      start: new Date(currentWeekStart),
      end: new Date(weekEnd)
    });
    
    // Move to next week
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }
  
  // Get the week range for this index
  const weekCount = Math.min(weeksInMonth.length, 4);
  if (weekIndex >= weekCount) return false;
  
  const weekRange = weeksInMonth[weekIndex] || 
    (weekIndex === 3 ? { 
      start: new Date(monthStart),
      end: new Date(monthStart)
    } : null);
  
  if (!weekRange) return false;
  
  // Check if the activity starts in this week
  return startDate >= weekRange.start && startDate <= weekRange.end;
};

const isLastWeekOfActivity = (activity, monthIndex, weekIndex, timelineMonths) => {
  if (!activity.START_DATE || !activity.END_DATE) return false;
  
  const endDate = new Date(activity.END_DATE);
  const month = timelineMonths[monthIndex];
  
  // Calculate week dates for this month
  const monthStart = new Date(month.date);
  const weeksInMonth = [];
  let currentWeekStart = new Date(monthStart);
  
  // Generate week date ranges
  while (currentWeekStart.getMonth() === monthStart.getMonth()) {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    
    weeksInMonth.push({
      start: new Date(currentWeekStart),
      end: new Date(weekEnd)
    });
    
    // Move to next week
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
  }
  
  // Get the week range for this index
  const weekCount = Math.min(weeksInMonth.length, 4);
  if (weekIndex >= weekCount) return false;
  
  const weekRange = weeksInMonth[weekIndex] || 
    (weekIndex === 3 ? { 
      start: new Date(monthStart),
      end: new Date(monthStart)
    } : null);
  
  if (!weekRange) return false;
  
  // Check if the activity ends in this week
  return endDate >= weekRange.start && endDate <= weekRange.end;
};

// First, add template definitions at the top of the file, after the existing constants
// Define audit template structures
const auditTemplates = {
  "SOX": [
    {
      phase: "Planning",
      order: 1,
      activities: [
        { activity: "Complete entity-level risk assessment and document materiality thresholds", status: "Not Started" },
        { activity: "Develop initial scoping memo and identify significant accounts/disclosures", status: "Not Started" },
        { activity: "Map financial statement assertions to in-scope business processes", status: "Not Started" },
        { activity: "Identify and document key systems and applications supporting in-scope processes", status: "Not Started" },
        { activity: "Conduct preliminary analytical procedures and identify high-risk areas", status: "Not Started" },
        { activity: "Develop SOX 404 assessment plan and testing approach per PCAOB standards", status: "Not Started" },
        { activity: "Schedule planning meetings with process/control owners", status: "Not Started" },
        { activity: "Set up SOX compliance documentation repository and audit trail", status: "Not Started" }
      ]
    },
    {
      phase: "Documentation",
      order: 2,
      activities: [
        { activity: "Document understanding of entity-level controls (ELCs) using COSO framework", status: "Not Started" },
        { activity: "Update narratives/flowcharts for key business processes (P2P, O2C, H2R, etc.)", status: "Not Started" },
        { activity: "Map identified risks to specific control activities (RCM development)", status: "Not Started" },
        { activity: "Document IT General Controls (ITGCs) for in-scope applications", status: "Not Started" },
        { activity: "Identify and document key reports used within control activities", status: "Not Started" },
        { activity: "Review and validate completeness of process documentation with control owners", status: "Not Started" },
        { activity: "Finalize Risk and Control Matrix (RCM) with control IDs and attributes", status: "Not Started" },
        { activity: "Document segregation of duties framework and matrix", status: "Not Started" }
      ]
    },
    {
      phase: "Testing",
      order: 3,
      activities: [
        { activity: "Perform design effectiveness testing of entity-level controls", status: "Not Started" },
        { activity: "Conduct design effectiveness testing of business process controls", status: "Not Started" },
        { activity: "Test operating effectiveness of automated controls and application configurations", status: "Not Started" },
        { activity: "Perform operating effectiveness testing of key manual controls", status: "Not Started" },
        { activity: "Execute ITGC testing (change management, access controls, operations)", status: "Not Started" },
        { activity: "Validate data integrity and completeness of system-generated reports", status: "Not Started" },
        { activity: "Perform quarterly review procedures for significant changes", status: "Not Started" },
        { activity: "Test remediated controls and document validation procedures", status: "Not Started" }
      ]
    },
    {
      phase: "Evaluation",
      order: 4,
      activities: [
        { activity: "Evaluate test of design failures and document design gaps", status: "Not Started" },
        { activity: "Analyze test of operating effectiveness exceptions", status: "Not Started" },
        { activity: "Perform root cause analysis for identified control deficiencies", status: "Not Started" },
        { activity: "Classify control deficiencies (deficiency, significant deficiency, material weakness)", status: "Not Started" },
        { activity: "Assess aggregation risk of multiple deficiencies", status: "Not Started" },
        { activity: "Document compensating controls and perform validation testing", status: "Not Started" },
        { activity: "Quantify potential financial statement impact of control deficiencies", status: "Not Started" },
        { activity: "Prepare detailed control deficiency summary for review", status: "Not Started" }
      ]
    },
    {
      phase: "Reporting",
      order: 5,
      activities: [
        { activity: "Draft preliminary SOX 404 assessment for management review", status: "Not Started" },
        { activity: "Prepare remediation plans for identified deficiencies", status: "Not Started" },
        { activity: "Schedule and conduct closing meeting with management", status: "Not Started" },
        { activity: "Present findings to Audit Committee with management responses", status: "Not Started" },
        { activity: "Finalize management representation letters for signing", status: "Not Started" },
        { activity: "Coordinate with external auditors on findings and remediation status", status: "Not Started" },
        { activity: "Prepare final management assessment of ICFR per SOX 404 requirements", status: "Not Started" },
        { activity: "Document lessons learned and recommendations for next audit cycle", status: "Not Started" }
      ]
    }
  ],
  "SOC": [
    {
      phase: "Planning",
      order: 1,
      activities: [
        { activity: "Define engagement scope and boundaries (SOC 1, SOC 2, criteria selection)", status: "Not Started" },
        { activity: "Complete independence assessment and document conclusions", status: "Not Started" },
        { activity: "Identify and document complementary user entity controls (CUECs)", status: "Not Started" },
        { activity: "Develop test plan aligned with AICPA attestation standards", status: "Not Started" },
        { activity: "Establish sampling methodology and population parameters", status: "Not Started" },
        { activity: "Develop service organizational readiness assessment checklist", status: "Not Started" },
        { activity: "Schedule kickoff meeting with service organization management", status: "Not Started" },
        { activity: "Identify subservice organizations and carve-out/inclusive approach", status: "Not Started" }
      ]
    },
    {
      phase: "System Description",
      order: 2,
      activities: [
        { activity: "Document infrastructure (physical and virtual servers, networks, etc.)", status: "Not Started" },
        { activity: "Map software components (applications, databases, middleware)", status: "Not Started" },
        { activity: "Document personnel roles and organizational structure", status: "Not Started" },
        { activity: "Detail procedures (automated and manual) within service provision", status: "Not Started" },
        { activity: "Document data (inputs, processing, outputs, reporting) flows", status: "Not Started" },
        { activity: "Map control environment and risk assessment processes", status: "Not Started" },
        { activity: "Detail monitoring activities and corrective processes", status: "Not Started" },
        { activity: "Document key system changes during the period under review", status: "Not Started" }
      ]
    },
    {
      phase: "Control Documentation",
      order: 3,
      activities: [
        { activity: "Document control objectives (SOC 1) or trust service criteria (SOC 2)", status: "Not Started" },
        { activity: "Map controls to Common Criteria (CC) requirements", status: "Not Started" },
        { activity: "Document Additional Criteria controls (Privacy, Availability, etc.)", status: "Not Started" },
        { activity: "Create control matrices with control IDs and descriptions", status: "Not Started" },
        { activity: "Document control owners and frequency of operation", status: "Not Started" },
        { activity: "Identify automated vs. manual controls and system dependencies", status: "Not Started" },
        { activity: "Document evidence requirements for each control", status: "Not Started" },
        { activity: "Validate control documentation with service organization", status: "Not Started" }
      ]
    },
    {
      phase: "Testing",
      order: 4,
      activities: [
        { activity: "Perform test of implementation for newly designed controls", status: "Not Started" },
        { activity: "Execute control operator interviews for evidence collection", status: "Not Started" },
        { activity: "Test access provisioning and deprovisioning controls", status: "Not Started" },
        { activity: "Validate change management procedures and approvals", status: "Not Started" },
        { activity: "Test environmental security and physical safeguards", status: "Not Started" },
        { activity: "Sample and test transaction processing controls (SOC 1)", status: "Not Started" },
        { activity: "Test incident management and problem resolution controls", status: "Not Started" },
        { activity: "Perform evidence-based testing of monitoring controls", status: "Not Started" },
        { activity: "Test encryption and data protection mechanisms", status: "Not Started" },
        { activity: "Validate subservice organization monitoring controls", status: "Not Started" }
      ]
    },
    {
      phase: "Reporting",
      order: 5,
      activities: [
        { activity: "Document testing exceptions and control gaps", status: "Not Started" },
        { activity: "Prepare initial report draft with testing results", status: "Not Started" },
        { activity: "Develop management response templates for exceptions", status: "Not Started" },
        { activity: "Conduct preliminary findings review with management", status: "Not Started" },
        { activity: "Update system description based on feedback", status: "Not Started" },
        { activity: "Prepare opinion language based on testing results", status: "Not Started" },
        { activity: "Complete quality review of SOC report", status: "Not Started" },
        { activity: "Finalize report with management representations", status: "Not Started" },
        { activity: "Issue final SOC report with appropriate distribution restrictions", status: "Not Started" }
      ]
    }
  ],
  "Operational": [
    {
      phase: "Planning",
      order: 1,
      activities: [
        { activity: "Conduct preliminary process performance assessment", status: "Not Started" },
        { activity: "Identify KPIs and operational metrics for evaluation", status: "Not Started" },
        { activity: "Develop operational risk assessment questionnaire", status: "Not Started" },
        { activity: "Complete industry benchmarking research for comparison", status: "Not Started" },
        { activity: "Identify and document key personnel for interviews", status: "Not Started" },
        { activity: "Map core operational processes and dependencies", status: "Not Started" },
        { activity: "Develop data request list for operational analysis", status: "Not Started" },
        { activity: "Schedule kickoff and planning meetings with stakeholders", status: "Not Started" }
      ]
    },
    {
      phase: "Process Analysis",
      order: 2,
      activities: [
        { activity: "Perform current state process mapping (SIPOC, VSM)", status: "Not Started" },
        { activity: "Identify process bottlenecks and constraint points", status: "Not Started" },
        { activity: "Calculate process cycle time and throughput metrics", status: "Not Started" },
        { activity: "Document handoffs and cross-functional dependencies", status: "Not Started" },
        { activity: "Quantify error rates and quality control points", status: "Not Started" },
        { activity: "Perform cost driver analysis for operational activities", status: "Not Started" },
        { activity: "Conduct lean waste assessment (8 wastes methodology)", status: "Not Started" },
        { activity: "Document technology enablement and automation opportunities", status: "Not Started" }
      ]
    },
    {
      phase: "Fieldwork",
      order: 3,
      activities: [
        { activity: "Perform process observation and time-motion studies", status: "Not Started" },
        { activity: "Conduct stakeholder interviews (management and staff)", status: "Not Started" },
        { activity: "Execute data analysis of operational metrics", status: "Not Started" },
        { activity: "Validate compliance with operational standards and policies", status: "Not Started" },
        { activity: "Test transactional efficiency and exception handling", status: "Not Started" },
        { activity: "Perform workforce capacity and utilization analysis", status: "Not Started" },
        { activity: "Analyze technology systems supporting operations", status: "Not Started" },
        { activity: "Document service level agreements and performance", status: "Not Started" },
        { activity: "Validate physical asset utilization and performance", status: "Not Started" }
      ]
    },
    {
      phase: "Analysis",
      order: 4,
      activities: [
        { activity: "Perform gap analysis against industry best practices", status: "Not Started" },
        { activity: "Calculate opportunity costs of inefficiencies", status: "Not Started" },
        { activity: "Develop process optimization scenarios", status: "Not Started" },
        { activity: "Model financial impact of potential improvements", status: "Not Started" },
        { activity: "Perform root cause analysis of operational issues", status: "Not Started" },
        { activity: "Prioritize improvement opportunities (impact vs. effort)", status: "Not Started" },
        { activity: "Create future state process design recommendations", status: "Not Started" },
        { activity: "Develop implementation roadmap for recommendations", status: "Not Started" }
      ]
    },
    {
      phase: "Reporting",
      order: 5,
      activities: [
        { activity: "Draft executive summary with key findings", status: "Not Started" },
        { activity: "Prepare detailed operational assessment report", status: "Not Started" },
        { activity: "Develop quantified business case for recommendations", status: "Not Started" },
        { activity: "Create implementation timeline with resource requirements", status: "Not Started" },
        { activity: "Present preliminary findings to management", status: "Not Started" },
        { activity: "Finalize recommendations with management input", status: "Not Started" },
        { activity: "Develop KPI framework for ongoing performance tracking", status: "Not Started" },
        { activity: "Prepare final presentation for executive leadership", status: "Not Started" }
      ]
    },
    {
      phase: "Follow-up",
      order: 6,
      activities: [
        { activity: "Conduct implementation planning workshops", status: "Not Started" },
        { activity: "Develop detailed action plans for key recommendations", status: "Not Started" },
        { activity: "Establish performance tracking dashboard for improvements", status: "Not Started" },
        { activity: "Facilitate implementation of quick wins", status: "Not Started" },
        { activity: "Monitor progress against implementation roadmap", status: "Not Started" },
        { activity: "Measure operational improvement results", status: "Not Started" },
        { activity: "Perform post-implementation review", status: "Not Started" },
        { activity: "Document lessons learned and best practices", status: "Not Started" }
      ]
    }
  ],
  "IT Audit": [
    {
      phase: "Planning",
      order: 1,
      activities: [
        { activity: "Develop IT risk assessment framework and scoring model", status: "Not Started" },
        { activity: "Document IT environment architecture and components", status: "Not Started" },
        { activity: "Map business processes to IT systems and dependencies", status: "Not Started" },
        { activity: "Identify and prioritize critical applications and infrastructure", status: "Not Started" },
        { activity: "Assess regulatory and compliance requirements (GDPR, HIPAA, etc.)", status: "Not Started" },
        { activity: "Document IT governance structure and oversight mechanisms", status: "Not Started" },
        { activity: "Develop testing approach for key IT domains", status: "Not Started" },
        { activity: "Schedule kickoff with IT management and key stakeholders", status: "Not Started" }
      ]
    },
    {
      phase: "IT Governance Review",
      order: 2,
      activities: [
        { activity: "Assess IT strategic planning and alignment with business objectives", status: "Not Started" },
        { activity: "Review IT policies, standards, and procedures for adequacy", status: "Not Started" },
        { activity: "Evaluate IT organizational structure and reporting lines", status: "Not Started" },
        { activity: "Assess IT risk management practices and integration", status: "Not Started" },
        { activity: "Review IT budgeting and resource allocation processes", status: "Not Started" },
        { activity: "Evaluate IT performance measurement and reporting", status: "Not Started" },
        { activity: "Assess vendor management practices and third-party oversight", status: "Not Started" },
        { activity: "Review project management methodology and governance", status: "Not Started" }
      ]
    },
    {
      phase: "Security Assessment",
      order: 3,
      activities: [
        { activity: "Review information security policy framework and implementation", status: "Not Started" },
        { activity: "Assess identity and access management controls", status: "Not Started" },
        { activity: "Evaluate network security architecture and controls", status: "Not Started" },
        { activity: "Test vulnerability management and patch processes", status: "Not Started" },
        { activity: "Review endpoint security controls and configurations", status: "Not Started" },
        { activity: "Assess data classification and protection mechanisms", status: "Not Started" },
        { activity: "Evaluate security incident response capabilities", status: "Not Started" },
        { activity: "Review security awareness and training program", status: "Not Started" },
        { activity: "Assess physical and environmental security controls", status: "Not Started" }
      ]
    },
    {
      phase: "Operations Review",
      order: 4,
      activities: [
        { activity: "Evaluate change management processes and controls", status: "Not Started" },
        { activity: "Assess IT service management and ITIL implementation", status: "Not Started" },
        { activity: "Review problem and incident management procedures", status: "Not Started" },
        { activity: "Evaluate capacity planning and performance monitoring", status: "Not Started" },
        { activity: "Assess database management and data integrity controls", status: "Not Started" },
        { activity: "Review backup and recovery procedures and testing", status: "Not Started" },
        { activity: "Evaluate business continuity and disaster recovery plans", status: "Not Started" },
        { activity: "Assess configuration management and asset inventory", status: "Not Started" }
      ]
    },
    {
      phase: "Application Controls",
      order: 5,
      activities: [
        { activity: "Evaluate application development and acquisition controls", status: "Not Started" },
        { activity: "Assess application authentication and authorization controls", status: "Not Started" },
        { activity: "Test input validation and processing controls", status: "Not Started" },
        { activity: "Evaluate output review and error handling processes", status: "Not Started" },
        { activity: "Assess interface controls and data transfer validation", status: "Not Started" },
        { activity: "Review application configuration and customization controls", status: "Not Started" },
        { activity: "Evaluate segregation of duties within applications", status: "Not Started" },
        { activity: "Assess application audit logging and monitoring", status: "Not Started" }
      ]
    },
    {
      phase: "Reporting",
      order: 6,
      activities: [
        { activity: "Document detailed findings with risk ratings", status: "Not Started" },
        { activity: "Develop recommendations aligned with framework standards", status: "Not Started" },
        { activity: "Quantify potential impact of IT control deficiencies", status: "Not Started" },
        { activity: "Present preliminary findings to IT management", status: "Not Started" },
        { activity: "Develop remediation plans with prioritization", status: "Not Started" },
        { activity: "Create IT risk heat map and executive dashboard", status: "Not Started" },
        { activity: "Prepare final IT audit report with appendices", status: "Not Started" },
        { activity: "Present findings to audit committee and board", status: "Not Started" }
      ]
    }
  ],
  "Financial Audit": [
    {
      phase: "Planning & Risk Assessment",
      order: 1,
      activities: [
        { activity: "Update understanding of entity and environment (ISA 315)", status: "Not Started" },
        { activity: "Perform preliminary analytical procedures on financial data", status: "Not Started" },
        { activity: "Evaluate internal control framework and reliability", status: "Not Started" },
        { activity: "Determine materiality levels (overall, performance, trivial)", status: "Not Started" },
        { activity: "Identify and assess fraud risk factors (ISA 240)", status: "Not Started" },
        { activity: "Develop risk assessment by financial statement area", status: "Not Started" },
        { activity: "Prepare audit strategy memorandum with approach", status: "Not Started" },
        { activity: "Develop detailed audit plan and resource allocation", status: "Not Started" }
      ]
    },
    {
      phase: "Internal Controls Testing",
      order: 2,
      activities: [
        { activity: "Map key transaction flows and control points", status: "Not Started" },
        { activity: "Evaluate design of financial reporting controls", status: "Not Started" },
        { activity: "Test implementation of control activities", status: "Not Started" },
        { activity: "Perform tests of operating effectiveness for key controls", status: "Not Started" },
        { activity: "Evaluate and test IT general controls supporting financials", status: "Not Started" },
        { activity: "Assess segregation of duties in financial processes", status: "Not Started" },
        { activity: "Document control deficiencies and impact on audit approach", status: "Not Started" },
        { activity: "Update risk assessment based on control testing results", status: "Not Started" }
      ]
    },
    {
      phase: "Substantive Testing",
      order: 3,
      activities: [
        { activity: "Perform detailed substantive testing on revenue recognition", status: "Not Started" },
        { activity: "Test purchase transactions and accounts payable completeness", status: "Not Started" },
        { activity: "Perform inventory observation and valuation testing", status: "Not Started" },
        { activity: "Test fixed asset additions, disposals, and depreciation", status: "Not Started" },
        { activity: "Perform bank reconciliation testing and cash procedures", status: "Not Started" },
        { activity: "Test accrued liabilities and expense cutoff", status: "Not Started" },
        { activity: "Perform detailed payroll testing and analytics", status: "Not Started" },
        { activity: "Test equity transactions and retained earnings", status: "Not Started" },
        { activity: "Perform detailed testing of significant estimates", status: "Not Started" },
        { activity: "Test related party transactions and disclosures", status: "Not Started" }
      ]
    },
    {
      phase: "Completion",
      order: 4,
      activities: [
        { activity: "Perform final analytical procedures on draft financials", status: "Not Started" },
        { activity: "Review subsequent events through reporting date", status: "Not Started" },
        { activity: "Obtain written management representations", status: "Not Started" },
        { activity: "Perform going concern assessment", status: "Not Started" },
        { activity: "Complete disclosure checklist for financial statements", status: "Not Started" },
        { activity: "Evaluate uncorrected misstatements and adjusting entries", status: "Not Started" },
        { activity: "Prepare summary of audit differences", status: "Not Started" },
        { activity: "Complete documentation and cross-referencing", status: "Not Started" },
        { activity: "Conduct engagement quality control review", status: "Not Started" }
      ]
    },
    {
      phase: "Reporting",
      order: 5,
      activities: [
        { activity: "Draft audit opinion and evaluate modifications needed", status: "Not Started" },
        { activity: "Prepare key audit matters for enhanced reporting", status: "Not Started" },
        { activity: "Complete detailed review of financial statements", status: "Not Started" },
        { activity: "Prepare management letter with control recommendations", status: "Not Started" },
        { activity: "Develop observation presentation for audit committee", status: "Not Started" },
        { activity: "Conduct closing meeting with management", status: "Not Started" },
        { activity: "Finalize and issue audit report with opinion", status: "Not Started" },
        { activity: "Complete comprehensive audit file documentation", status: "Not Started" }
      ]
    }
  ]
};

// Add this function to sort phases by order
const sortPhasesByOrder = (phases) => {
  return [...phases].sort((a, b) => {
    // First, try to find order in the template definitions
    const templatePhases = Object.values(auditTemplates).flat();
    const phaseA = templatePhases.find(p => p.phase === a);
    const phaseB = templatePhases.find(p => p.phase === b);
    
    // If both phases have an order, sort by order
    if (phaseA?.order && phaseB?.order) {
      return phaseA.order - phaseB.order;
    }
    
    // Default sorting by common audit phases
    const defaultOrder = {
      'Planning': 1,
      'Scoping': 2,
      'Fieldwork': 3,
      'Execution': 3,  // Alternate name for fieldwork
      'Testing': 3,    // Alternate name for fieldwork
      'Reporting': 4,
      'Follow-up': 5
    };
    
    const orderA = defaultOrder[a] || 99;
    const orderB = defaultOrder[b] || 99;
    
    return orderA - orderB;
  });
};

// Before fetching plan items, add a function to sort by sequence
const sortBySequence = (items) => {
  // Group items by phase
  const groupedByPhase = {};
  items.forEach(item => {
    if (!groupedByPhase[item.PHASE]) {
      groupedByPhase[item.PHASE] = [];
    }
    groupedByPhase[item.PHASE].push(item);
  });
  
  // Sort each phase's items by sequence
  Object.keys(groupedByPhase).forEach(phase => {
    groupedByPhase[phase].sort((a, b) => (a.SEQUENCE || 0) - (b.SEQUENCE || 0));
  });
  
  // Flatten back to a single array, with phases in standard order
  const sortedPhases = sortPhasesByOrder(Object.keys(groupedByPhase));
  return sortedPhases.flatMap(phase => groupedByPhase[phase]);
};

// Add these functions before the PlanningDetails component
// Function to apply cell styles for Excel export
const applyCellStyles = (worksheet, cell, style) => {
  if (!worksheet['!styles']) worksheet['!styles'] = {};
  worksheet['!styles'][cell] = style;
};

// Function to create cell ranges for merged cells
const createMergeCell = (startCell, endCell) => {
  return { s: startCell, e: endCell };
};

// Add a new function to format Excel data with colors matching the UI
const formatExcelData = (wb) => {
  // Define status colors for Excel (simpler formats for Excel compatibility)
  const excelStatusColors = {
    "Not Started": { bgColor: "D3D3D3", fontColor: "333333" }, // Grey
    "In Progress": { bgColor: "BBDEFB", fontColor: "0D47A1" }, // Blue
    "Completed": { bgColor: "C8E6C9", fontColor: "1B5E20" },   // Green
    "Past Due": { bgColor: "FFCDD2", fontColor: "B71C1C" }     // Red
  };
  
  // Format each worksheet in the workbook
  wb.SheetNames.forEach(sheetName => {
    const ws = wb.Sheets[sheetName];
    
    // Skip empty worksheets
    if (!ws['!ref']) return;
    
    // Set up custom styles for Excel
    if (!ws['!cols']) ws['!cols'] = [];
    
    // Initialize styles property if it doesn't exist
    if (!ws.Styles) ws.Styles = {};
    
    // Identify range of cells in the worksheet
    const range = XLSX.utils.decode_range(ws['!ref']);
    
    // Process each cell in the worksheet
    for (let R = range.s.r; R <= range.e.r; R++) {
      for (let C = range.s.c; C <= range.e.c; C++) {
        const cellRef = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[cellRef];
        
        // Skip if cell doesn't exist
        if (!cell) continue;
        
        // Apply header formatting
        if (R === 0 || (sheetName === "Summary" && R < 6)) {
          // Header row formatting - bold, larger font
          cell.s = {
            font: { bold: true, sz: 14, color: { rgb: "2C3E50" } },
            fill: { fgColor: { rgb: "ECF0F1" } }
          };
        }
        
        // Format Status column in various sheets
        if (sheetName !== "Gantt Chart" && sheetName !== "Enhanced Gantt") {
          // Check if we're in a status cell (usually column 5 or 6 depending on sheet)
          const statusColIndex = sheetName === "Summary" ? 0 : 5; // Adjust as needed
          
          if (C === statusColIndex && R > 1) {
            const cellValue = cell.v;
            
            if (cellValue) {
              let statusStyle = {};
              
              // Apply color based on status
              if (cellValue === "Completed") {
                statusStyle = {
                  font: { color: { rgb: excelStatusColors["Completed"].fontColor }, bold: true },
                  fill: { fgColor: { rgb: excelStatusColors["Completed"].bgColor } }
                };
              } else if (cellValue === "In Progress") {
                statusStyle = {
                  font: { color: { rgb: excelStatusColors["In Progress"].fontColor }, bold: true },
                  fill: { fgColor: { rgb: excelStatusColors["In Progress"].bgColor } }
                };
              } else if (cellValue === "Past Due") {
                statusStyle = {
                  font: { color: { rgb: excelStatusColors["Past Due"].fontColor }, bold: true },
                  fill: { fgColor: { rgb: excelStatusColors["Past Due"].bgColor } }
                };
              } else if (cellValue === "Not Started") {
                statusStyle = {
                  font: { color: { rgb: excelStatusColors["Not Started"].fontColor } },
                  fill: { fgColor: { rgb: excelStatusColors["Not Started"].bgColor } }
                };
              }
              
              cell.s = statusStyle;
            }
          }
        }
        
        // Special formatting for Gantt Chart sheet
        if (sheetName === "Gantt Chart" && R > 2) {
          // For each day cell, apply color based on status indicator
          if (C > 1) {
            if (cell.v === 'C') { // Completed
              cell.s = {
                font: { color: { rgb: "FFFFFF" }, bold: true },
                fill: { fgColor: { rgb: "22C55E" } } // Green
              };
              cell.v = '■'; // Replace with a block character
            } else if (cell.v === 'I') { // In Progress
              cell.s = {
                font: { color: { rgb: "FFFFFF" }, bold: true },
                fill: { fgColor: { rgb: "0EA5E9" } } // Blue
              };
              cell.v = '■'; // Replace with a block character
            } else if (cell.v === 'P') { // Past Due
              cell.s = {
                font: { color: { rgb: "FFFFFF" }, bold: true },
                fill: { fgColor: { rgb: "EF4444" } } // Red
              };
              cell.v = '!'; // Exclamation mark for past due
            } else if (cell.v === 'N') { // Not Started
              cell.s = {
                font: { color: { rgb: "333333" } },
                fill: { fgColor: { rgb: "94A3B8" } } // Grey
              };
              cell.v = '■'; // Replace with a block character
            }
          }
        }
        
        // Enhanced Gantt chart visual bar formatting
        if (sheetName === "Enhanced Gantt" && C === 9 && R > 5) { // Visual timeline column
          const cellValue = cell.v || '';
          if (cellValue) {
            // Apply the right color based on the character used
            if (cellValue.includes('█')) { // Completed
              cell.s = {
                font: { color: { rgb: "22C55E" }, bold: true }
              };
            } else if (cellValue.includes('▓')) { // In Progress
              cell.s = {
                font: { color: { rgb: "0EA5E9" }, bold: true }
              };
            } else if (cellValue.includes('!')) { // Past Due
              cell.s = {
                font: { color: { rgb: "EF4444" }, bold: true }
              };
            } else if (cellValue.includes('▒')) { // Not Started
              cell.s = {
                font: { color: { rgb: "94A3B8" } }
              };
            }
          }
        }
      }
    }
  });
  
  return wb;
};

// Now add a function in the component to apply a template
const PlanningDetails = () => {
  const { id, document_type = 'General' } = useParams();
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState(null);
  const [planItems, setPlanItems] = useState([]);
  const [internalAuditUsers, setInternalAuditUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  
  // Document type info
  const [documentInfo, setDocumentInfo] = useState({
    name: document_type === 'General' ? 'General Audit Plan' : 
          document_type === 'IT Audit' ? 'IT Audit Plan' :
          document_type === 'Financial Audit' ? 'Financial Audit Plan' :
          document_type === 'Operational Audit' ? 'Operational Audit Plan' :
          document_type === 'SOX' ? 'SOX Compliance Plan' :
          document_type === 'SOC' ? 'SOC Audit Plan' : 'Custom Plan',
    color: document_type === 'General' ? '#2563eb' : 
           document_type === 'IT Audit' ? '#16a34a' :
           document_type === 'Financial Audit' ? '#9333ea' :
           document_type === 'Operational Audit' ? '#f59e0b' :
           document_type === 'SOX' ? '#dc2626' :
           document_type === 'SOC' ? '#0891b2' : '#6b7280'
  });
  
  // Form states
  const [phase, setPhase] = useState('');
  const [activity, setActivity] = useState('');
  const [owner, setOwner] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [status, setStatus] = useState('Not Started');

  // Timeline state as before
  const [timelineStartDate, setTimelineStartDate] = useState(new Date());
  const [jumpToDate, setJumpToDate] = useState(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [extendedView, setExtendedView] = useState(false);
  const [zoomLevel, setZoomLevel] = useState('normal'); // compact, normal, detailed
  const [activitiesView, setActivitiesView] = useState('card'); // card, list

  // Add state for timeline scheduling
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [scheduleStartDate, setScheduleStartDate] = useState(null);
  const [scheduleEndDate, setScheduleEndDate] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);

  // Add these new functions within the PlanningDetails component
  
  // Add function to handle quick add activity inline
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddPhase, setQuickAddPhase] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Quick add state for activity insertion
  const [insertAfter, setInsertAfter] = useState(null);
  const [insertPhase, setInsertPhase] = useState('');
  const [insertPosition, setInsertPosition] = useState(null);
  
  // Add state for delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState(null);

  // Add this new state for the delete plan confirmation dialog
  const [deletePlanDialogOpen, setDeletePlanDialogOpen] = useState(false);

  // Add new state for export loading
  const [exportLoading, setExportLoading] = useState(false);

  // Add state for view menu
  const [viewMenuAnchor, setViewMenuAnchor] = useState(null);

  // Add state for status update popover
  const [statusPopoverAnchorEl, setStatusPopoverAnchorEl] = useState(null);
  const [selectedStatusActivity, setSelectedStatusActivity] = useState(null);

  // Handle opening the status popover 
  const handleStatusPopoverOpen = (event, activity) => {
    // Prevent event propagation to avoid triggering cell selection
    event.stopPropagation();
    event.preventDefault();
    setStatusPopoverAnchorEl(event.currentTarget);
    setSelectedStatusActivity(activity);
  };

  // Handle closing the status popover
  const handleStatusPopoverClose = () => {
    setStatusPopoverAnchorEl(null);
    setSelectedStatusActivity(null);
  };

  // Handle status change from the popover
  const handleStatusChangeFromTimeline = async (newStatus) => {
    if (!selectedStatusActivity) return;
    
    try {
      await handleStatusChange(selectedStatusActivity.id, newStatus);
      handleStatusPopoverClose();
    } catch (error) {
      console.error('Error updating status from timeline:', error);
    }
  };

  // Add a new function to clear timeline dates for an activity
  const handleClearDates = async (activityId) => {
    if (!activityId) return;
    
    try {
      // Find the activity
      const activity = planItems.find(item => item.id === activityId);
      if (!activity) return;
      
      // Update with cleared dates
      await auditService.updateAuditPlan(activityId, {
        ...activity,
        START_DATE: null,
        END_DATE: null
      });
      
      // Update local state
      setPlanItems(planItems.map(item => 
        item.id === activityId ? { ...item, START_DATE: null, END_DATE: null } : item
      ));
      
      // Close the popover
      handleStatusPopoverClose();
      
      enqueueSnackbar('Timeline dates cleared successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error clearing timeline dates:', error);
      enqueueSnackbar('Failed to clear timeline dates', { variant: 'error' });
    }
  };

  // Add a function to edit timeline dates for an existing activity
  const handleEditTimeline = (activity) => {
    if (!activity) return;
    
    // Set the activity being edited
    setSelectedActivity(activity);
    
    // Set the start and end dates from the existing activity
    setScheduleStartDate(activity.START_DATE ? new Date(activity.START_DATE) : null);
    setScheduleEndDate(activity.END_DATE ? new Date(activity.END_DATE) : null);
    
    // Close the status popover first
    handleStatusPopoverClose();
    
    // Open the scheduling dialog with pre-filled dates
    setScheduleDialogOpen(true);
  };

  // Add an effect to reset the plan items when document_type changes
  useEffect(() => {
    // Clear the plan items when the document type changes to prevent showing stale data
    setPlanItems([]);
    
    // Update document info when document type changes
    setDocumentInfo({
      name: document_type === 'General' ? 'General Audit Plan' : 
            document_type === 'IT Audit' ? 'IT Audit Plan' :
            document_type === 'Financial Audit' ? 'Financial Audit Plan' :
            document_type === 'Operational Audit' ? 'Operational Audit Plan' :
            document_type === 'SOX' ? 'SOX Compliance Plan' :
            document_type === 'SOC' ? 'SOC Audit Plan' : 'Custom Plan',
      color: document_type === 'General' ? '#2563eb' : 
             document_type === 'IT Audit' ? '#16a34a' :
             document_type === 'Financial Audit' ? '#9333ea' :
             document_type === 'Operational Audit' ? '#f59e0b' :
             document_type === 'SOX' ? '#dc2626' :
             document_type === 'SOC' ? '#0891b2' : '#6b7280'
    });
    
    console.log(`[DEBUG] Document type changed to: ${document_type}`);
  }, [document_type]);

  // Add this function to handle deleting the entire plan
  const handleDeleteEntirePlan = async () => {
    try {
      setLoading(true);
      
      // Get all plan items for this document type only (not the entire project)
      const planItemsToDelete = planItems.map(item => item.id);
      
      // Delete each plan item
      for (const itemId of planItemsToDelete) {
        await auditService.deleteAuditPlan(itemId);
      }
      
      // Clear the plan items from state
      setPlanItems([]);
      
      setDeletePlanDialogOpen(false);
      setLoading(false);
      
      enqueueSnackbar(`Deleted all ${planItemsToDelete.length} activities from the ${document_type} audit plan`, { 
        variant: 'success' 
      });
      
      // Navigate back to the documents list
      navigate(`/audit/projects/${id}/planning`);
    } catch (error) {
      console.error('Error deleting plan:', error);
      enqueueSnackbar('Failed to delete the audit plan', { variant: 'error' });
      setLoading(false);
      setDeletePlanDialogOpen(false);
    }
  };

  // Handle opening the quick add menu
  const handleQuickAddClick = (event, phase) => {
    setQuickAddPhase(phase);
    setAnchorEl(event.currentTarget);
    setQuickAddOpen(true);
  };
  
  // Handle closing the quick add menu
  const handleQuickAddClose = () => {
    setQuickAddOpen(false);
    setAnchorEl(null);
  };
  
  // Handle quick add submission
  const handleQuickAdd = async (activityName) => {
    if (!quickAddPhase || !activityName) {
      enqueueSnackbar('Please specify an activity name', { variant: 'warning' });
      return;
    }
    
    try {
      const formData = {
        PHASE: quickAddPhase,
        ACTIVITY: activityName,
        OWNER: null,
        START_DATE: null,
        END_DATE: null,
        STATUS: 'Not Started',
        AUDIT_ID: id,
        COMPANY_ID: selectedProject.COMPANY_ID?.id || null,
        DOCUMENT_TYPE: document_type // Add document type to all new items
      };
      
      // Create new activity
      await auditService.createAuditPlan(formData);
      
      // Refresh data
      const newPlanData = await auditService.fetchAuditPlansByType(id, document_type);
      setPlanItems(newPlanData);
      
      // Close the quick add menu
      handleQuickAddClose();
      
      enqueueSnackbar('Activity added successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error adding activity:', error);
      enqueueSnackbar('Failed to add activity', { variant: 'error' });
    }
  };
  
  // Modify handleQuickDelete to show confirmation first
  const confirmDelete = (activityId, activityName) => {
    setActivityToDelete({ id: activityId, name: activityName });
    setDeleteDialogOpen(true);
  };
  
  // Execute the delete after confirmation
  const executeDelete = async () => {
    if (!activityToDelete) return;
    
    try {
      // Find the item we're deleting
      const itemToDelete = planItems.find(item => item.id === activityToDelete.id);
      if (!itemToDelete) {
        setDeleteDialogOpen(false);
        setActivityToDelete(null);
        return;
      }
      
      // Get all items in the same phase with higher sequence
      const phaseItems = planItems.filter(item => 
        item.PHASE === itemToDelete.PHASE && 
        item.id !== itemToDelete.id &&
        (item.SEQUENCE || 0) > (itemToDelete.SEQUENCE || 0)
      );
      
      // Delete the item
      await auditService.deleteAuditPlan(activityToDelete.id);
      
      // Adjust sequences of remaining items
      for (const item of phaseItems) {
        await auditService.updateAuditPlan(item.id, {
          ...item,
          SEQUENCE: (item.SEQUENCE || 0) - 1
        });
      }
      
      // Fetch updated data
      const newPlanData = await auditService.fetchAuditPlansByType(id, document_type);
      const sortedPlanData = sortBySequence(newPlanData);
      setPlanItems(sortedPlanData);
      
      enqueueSnackbar('Activity deleted successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting activity:', error);
      enqueueSnackbar('Failed to delete activity', { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setActivityToDelete(null);
    }
  };
  
  // Navigate back to planning documents list
  const handleBackToDocuments = () => {
    navigate(`/audit/projects/${id}/planning`);
  };
  
  // Clone activity
  const handleCloneActivity = async (activity) => {
    try {
      // Find the highest sequence for this phase
      const phaseItems = planItems.filter(item => item.PHASE === activity.PHASE);
      const maxSequence = phaseItems.length > 0 
        ? Math.max(...phaseItems.map(item => item.SEQUENCE || 0)) 
        : -1;
      
      const cloneData = {
        ...activity,
        ACTIVITY: `${activity.ACTIVITY} (Copy)`,
        SEQUENCE: maxSequence + 1,
        id: undefined
      };
      
      delete cloneData.id;
      delete cloneData.owner_details;
      
      // Create the cloned activity
      await auditService.createAuditPlan(cloneData);
      
      // Refresh data
      const newPlanData = await auditService.fetchAuditPlansByType(id, document_type);
      const sortedPlanData = sortBySequence(newPlanData);
      setPlanItems(sortedPlanData);
      
      enqueueSnackbar('Activity cloned successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error cloning activity:', error);
      enqueueSnackbar('Failed to clone activity', { variant: 'error' });
    }
  };

  // Fetch project details, audit plans and internal audit users
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log(`[DEBUG] Fetching data for document type: '${document_type}'`);
        
        // Fetch project details
        const projectData = await auditService.fetchProjectsById(id);
        setSelectedProject(projectData);
        
        // Fetch audit plans for this project filtered by document type
        console.log(`[DEBUG] Calling fetchAuditPlansByType with id=${id}, document_type=${document_type}`);
        const planData = await auditService.fetchAuditPlansByType(id, document_type);
        console.log(`[DEBUG] Received ${planData.length} plan items for document_type='${document_type}'`);
        console.log('[DEBUG] First few items:', planData.slice(0, 2).map(item => ({
          id: item.id,
          phase: item.PHASE,
          activity: item.ACTIVITY,
          documentType: item.DOCUMENT_TYPE
        })));
        
        // Filter to ensure only items with matching document_type are included (double-check)
        const filteredPlanData = planData.filter(item => item.DOCUMENT_TYPE === document_type);
        console.log(`[DEBUG] After filtering, have ${filteredPlanData.length} items with exact document_type match`);
        
        // If there's a mismatch, log a warning
        if (filteredPlanData.length !== planData.length) {
          console.warn(`[WARNING] API returned items with non-matching document types! Expected: '${document_type}'`);
          console.warn('[WARNING] Document types in response:', [...new Set(planData.map(item => item.DOCUMENT_TYPE))]);
        }
        
        // If it's a custom document type, get the custom name
        if (document_type === 'Custom' && filteredPlanData.length > 0 && filteredPlanData[0].CUSTOM_NAME) {
          setDocumentInfo(prev => ({
            ...prev,
            name: filteredPlanData[0].CUSTOM_NAME
          }));
        }
        
        // Sort by sequence within each phase
        const sortedPlanData = sortBySequence(filteredPlanData);
        setPlanItems(sortedPlanData);
        
        // Fetch internal audit users for this project
        try {
          const userData = await auditService.fetchInternalAuditUsers(id);
          setInternalAuditUsers(userData);
        } catch (userError) {
          console.error('Error fetching internal audit users:', userError);
            setInternalAuditUsers([]);
        }
        
        setLoading(false);
      } catch (error) {
        console.error(`Error fetching data for document type ${document_type}:`, error);
        enqueueSnackbar(`Failed to load planning details for ${document_type}`, { variant: 'error' });
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, document_type]);

  // Add an effect to enforce extended view when in compact mode
  useEffect(() => {
    if (zoomLevel === 'compact' && !extendedView) {
      setExtendedView(true);
    }
  }, [zoomLevel, extendedView]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!phase || !activity || !startDate || !endDate) {
      enqueueSnackbar('Please fill all required fields', { variant: 'warning' });
      return;
    }
    
    try {
      const formData = {
        PHASE: phase,
        ACTIVITY: activity,
        OWNER: owner || null,
        START_DATE: startDate ? startDate.toISOString().split('T')[0] : null,
        END_DATE: endDate ? endDate.toISOString().split('T')[0] : null,
        STATUS: status,
        AUDIT_ID: id,
        COMPANY_ID: selectedProject.COMPANY_ID?.id || null,
        DOCUMENT_TYPE: document_type // Add document type to all new items
      };
      
      if (editItemId) {
        // Update existing item
        await auditService.updateAuditPlan(editItemId, formData);
        enqueueSnackbar('Plan item updated successfully', { variant: 'success' });
      } else {
        // Create new item
        await auditService.createAuditPlan(formData);
        enqueueSnackbar('Plan item added successfully', { variant: 'success' });
      }
      
      // Refresh data
      const newPlanData = await auditService.fetchAuditPlansByType(id, document_type);
      setPlanItems(newPlanData);
      resetForm();
    } catch (error) {
      console.error('Error saving plan item:', error);
      enqueueSnackbar('Failed to save plan item', { variant: 'error' });
    }
  };

  // Reset form
  const resetForm = () => {
    setPhase('');
    setActivity('');
    setOwner('');
    setStartDate(null);
    setEndDate(null);
    setStatus('Not Started');
    setEditItemId(null);
    setFormOpen(false);
  };

  // Edit plan item
  const handleEdit = (item) => {
    setPhase(item.PHASE);
    setActivity(item.ACTIVITY);
    setOwner(item.OWNER?.id || '');
    setStartDate(item.START_DATE ? new Date(item.START_DATE) : null);
    setEndDate(item.END_DATE ? new Date(item.END_DATE) : null);
    setStatus(item.STATUS);
    setEditItemId(item.id);
    setFormOpen(true);
  };

  // Delete plan item
  const handleDelete = async (id) => {
    try {
      await auditService.deleteAuditPlan(id);
      
      // Remove item from state
      setPlanItems(planItems.filter(item => item.id !== id));
      enqueueSnackbar('Plan item deleted successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting plan item:', error);
      enqueueSnackbar('Failed to delete plan item', { variant: 'error' });
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Update status
  const handleStatusChange = async (id, newStatus) => {
    try {
      // Find the item
      const item = planItems.find(item => item.id === id);
      if (!item) return;
      
      // Update the status
      await auditService.updateAuditPlan(id, { ...item, STATUS: newStatus });
      
      // Update local state
      setPlanItems(planItems.map(item => 
        item.id === id ? { ...item, STATUS: newStatus } : item
      ));
      
      enqueueSnackbar('Status updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating status:', error);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
    }
  };

  // Navigate timeline to previous 3 months
  const handlePreviousMonths = () => {
    const newDate = new Date(timelineStartDate);
    newDate.setMonth(newDate.getMonth() - 3);
    setTimelineStartDate(newDate);
  };
  
  // Navigate timeline to next 3 months
  const handleNextMonths = () => {
    const newDate = new Date(timelineStartDate);
    newDate.setMonth(newDate.getMonth() + 3);
    setTimelineStartDate(newDate);
  };
  
  // Jump to specific date
  const handleJumpToDate = () => {
    if (jumpToDate) {
      setTimelineStartDate(jumpToDate);
      setDatePickerOpen(false);
    }
  };

  // Handle click on a week cell to schedule an activity
  const handleCellClick = (phase, activity, monthIndex, weekIndex) => {
    // Only allow scheduling for activities without dates
    if (!activity) return;
    
    // Get the month and calculate week dates
    const month = getTimelineMonths(timelineStartDate, extendedView)[monthIndex];
    if (!month) return;
    
    // Calculate approximate start and end dates for the week
    const monthDate = new Date(month.date);
    const weekStartDate = new Date(monthDate);
    weekStartDate.setDate(weekStartDate.getDate() + (weekIndex * 7));
    
    const weekEndDate = new Date(weekStartDate);
    weekEndDate.setDate(weekEndDate.getDate() + 6);
    
    // Save the information for the scheduling dialog
    setSelectedActivity({
      phase,
      activity,
      monthIndex,
      weekIndex,
      startDate: weekStartDate,
      endDate: weekEndDate
    });
    setScheduleStartDate(weekStartDate);
    setScheduleEndDate(weekEndDate);
    
    // Open the scheduling dialog
    setScheduleDialogOpen(true);
  };

  // Handle mouse down on a cell to start selection
  const handleMouseDown = (phase, activity, monthIndex, weekIndex) => {
    // Only allow scheduling for activities
    if (!activity) return;
    
    console.log('[DEBUG] handleMouseDown called for activity:', activity.ACTIVITY || activity.id);
    
    // Check if the activity already has scheduled dates
    const hasScheduledDates = activity.START_DATE && activity.END_DATE;
    
    // If the activity already has dates set, don't trigger the scheduling behavior
    if (hasScheduledDates) {
      // Let the status update popover handle the click instead
      console.log('[DEBUG] Activity already has dates, not starting selection');
      return;
    }
    
    // Get the month and calculate week dates
    const month = getTimelineMonths(timelineStartDate, extendedView)[monthIndex];
    if (!month) return;
    
    // Calculate approximate start date for the week
    const monthDate = new Date(month.date);
    const weekStartDate = new Date(monthDate);
    weekStartDate.setDate(weekStartDate.getDate() + (weekIndex * 7));
    
    // Calculate week end date
    const weekEndDate = new Date(weekStartDate);
    weekEndDate.setDate(weekEndDate.getDate() + 6);
    
    console.log('[DEBUG] Starting selection for activity:', {
      activity: activity.ACTIVITY || activity.id,
      phase,
      startDate: format(weekStartDate, 'MMM d, yyyy'),
      endDate: format(weekEndDate, 'MMM d, yyyy'),
    });
    
    // Start the selection process
    setIsSelecting(true);
    setSelectionStart({ monthIndex, weekIndex, date: weekStartDate });
    setSelectionEnd({ monthIndex, weekIndex, date: weekStartDate });
    
    // Set the activity being scheduled
    setSelectedActivity(activity);
    
    // Initialize the date range
    setScheduleStartDate(weekStartDate);
    setScheduleEndDate(weekEndDate);
    
    // Add event listeners for mousemove and mouseup to the document
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Add new function to update selection
  const updateSelection = (monthIndex, weekIndex) => {
    if (!isSelecting || !selectionStart) return;
    
    // Get the month
    const month = getTimelineMonths(timelineStartDate, extendedView)[monthIndex];
    if (!month) return;
    
    // Calculate dates for this week
    const monthDate = new Date(month.date);
    const weekStartDate = new Date(monthDate);
    weekStartDate.setDate(weekStartDate.getDate() + (weekIndex * 7));
    
    const weekEndDate = new Date(weekStartDate);
    weekEndDate.setDate(weekEndDate.getDate() + 6);
    
    // Update the selection end
    setSelectionEnd({ monthIndex, weekIndex, date: weekStartDate });
    
    // Calculate the correct start and end dates based on selection direction
    const startCell = selectionStart;
    const endCell = { monthIndex, weekIndex };
    
    const isForward = 
      startCell.monthIndex < endCell.monthIndex || 
      (startCell.monthIndex === endCell.monthIndex && startCell.weekIndex <= endCell.weekIndex);
      
    if (isForward) {
      setScheduleStartDate(selectionStart.date);
      setScheduleEndDate(weekEndDate);
    } else {
      setScheduleStartDate(weekStartDate);
      setScheduleEndDate(new Date(selectionStart.date.getTime() + (6 * 24 * 60 * 60 * 1000))); // Add 6 days
    }
  };
  
  // Simplify handleMouseMove to use direct DOM operations less frequently
  const handleMouseMove = (e) => {
    // Only continue if we're in selection mode
    if (!isSelecting) return;
    
    // We'll handle most cases through the onMouseOver handler on cells
    // This is a fallback for when mouse moves too quickly between cells
    
    // Find the table cell element under the cursor
    const cellElement = document.elementFromPoint(e.clientX, e.clientY);
    if (!cellElement) return;
    
    // Find the parent table cell if we're hovering over a child element
    const findTableCell = (element) => {
      if (!element) return null;
      if (element.tagName === 'TD') return element;
      return findTableCell(element.parentElement);
    };
    
    const tableCellElement = findTableCell(cellElement);
    if (!tableCellElement) return;
    
    // Get the cell's data attributes for month and week
    const cellMonthIndex = tableCellElement.dataset.monthIndex;
    const cellWeekIndex = tableCellElement.dataset.weekIndex;
    
    if (cellMonthIndex === undefined || cellWeekIndex === undefined) return;
    
    // Update the selection
    updateSelection(parseInt(cellMonthIndex), parseInt(cellWeekIndex));
  };
  
  // Handle mouse up to finalize the selection
  const handleMouseUp = () => {
    // End the selection process
    setIsSelecting(false);
    
    // Remove the event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    
    // Open the scheduling dialog if we have a valid selection
    if (selectionStart && selectionEnd && selectedActivity) {
      console.log('[DEBUG] Opening scheduling dialog with selection:', {
        start: format(scheduleStartDate, 'MMM d, yyyy'),
        end: format(scheduleEndDate, 'MMM d, yyyy'),
        activity: selectedActivity.ACTIVITY || selectedActivity.id
      });
    setScheduleDialogOpen(true);
    } else {
      console.warn('[WARN] Missing data for scheduling dialog:', {
        selectionStart: !!selectionStart,
        selectionEnd: !!selectionEnd,
        selectedActivity: !!selectedActivity
      });
    }
  };
  
  // Enhance the isCellSelected function to make selection more visually obvious
  // and only highlight cells for the specific activity being scheduled
  const isCellSelected = (monthIndex, weekIndex, activityId) => {
    if (!selectionStart || !selectionEnd || !isSelecting || !selectedActivity) return false;
    
    // Only highlight cells for the current activity being scheduled
    if (activityId !== selectedActivity.id) return false;
    
    // Calculate the boundaries of the selection
    const minMonthIndex = Math.min(selectionStart.monthIndex, selectionEnd.monthIndex);
    const maxMonthIndex = Math.max(selectionStart.monthIndex, selectionEnd.monthIndex);
    
    // If outside the month range, not selected
    if (monthIndex < minMonthIndex || monthIndex > maxMonthIndex) return false;
    
    // For months within the range, check the week
    if (minMonthIndex === maxMonthIndex) {
      // Same month - check if week is within the selected week range
      const minWeekIndex = Math.min(selectionStart.weekIndex, selectionEnd.weekIndex);
      const maxWeekIndex = Math.max(selectionStart.weekIndex, selectionEnd.weekIndex);
      return weekIndex >= minWeekIndex && weekIndex <= maxWeekIndex;
    } else if (monthIndex === minMonthIndex) {
      // First month in range - check if week is >= the start week
      const startWeekIndex = selectionStart.monthIndex === minMonthIndex ? 
                            selectionStart.weekIndex : selectionEnd.weekIndex;
      return weekIndex >= startWeekIndex;
    } else if (monthIndex === maxMonthIndex) {
      // Last month in range - check if week is <= the end week
      const endWeekIndex = selectionStart.monthIndex === maxMonthIndex ? 
                          selectionStart.weekIndex : selectionEnd.weekIndex;
      return weekIndex <= endWeekIndex;
    }
    
    // If it's a month in the middle of the range, it's always selected
    return true;
  };

  // Handle saving the schedule for an activity
  const handleSaveSchedule = async () => {
    if (!selectedActivity || !scheduleStartDate || !scheduleEndDate) {
      enqueueSnackbar('Please select valid dates', { variant: 'warning' });
      return;
    }
    
    try {
      console.log('[DEBUG] Saving schedule for activity:', selectedActivity);
      
      // Find the activity in the plan items
      const activityToUpdate = planItems.find(item => item.id === selectedActivity.id);
      
      if (!activityToUpdate) {
        console.error('[ERROR] Activity not found in plan items:', selectedActivity);
        enqueueSnackbar('Error: Activity not found', { variant: 'error' });
        return;
      }
      
      // Convert dates to string format for API
      const startDateStr = scheduleStartDate.toISOString().split('T')[0];
      const endDateStr = scheduleEndDate.toISOString().split('T')[0];
      
      // Create updated activity object
      const updatedActivity = {
        ...activityToUpdate,
        START_DATE: startDateStr,
        END_DATE: endDateStr
      };
      
      // Save to database
      await auditService.updateAuditPlan(selectedActivity.id, updatedActivity);
      
      // Update local state
      setPlanItems(planItems.map(item => 
        item.id === selectedActivity.id ? updatedActivity : item
      ));
      
      // Close dialog and reset
      setScheduleDialogOpen(false);
      setSelectedActivity(null);
      setSelectionStart(null);
      setSelectionEnd(null);
      
      enqueueSnackbar('Activity scheduled successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error scheduling activity:', error);
      enqueueSnackbar('Failed to schedule activity', { variant: 'error' });
    }
  };

  // Enhance the quick add function to support new phases and sequences
  const handleQuickAddWithPosition = async (activityName, phase, position = null, afterActivityId = null) => {
    if (!activityName) {
      enqueueSnackbar('Please specify an activity name', { variant: 'warning' });
      return;
    }
    
    // Handle new phase creation
    let finalPhase = phase;
    if (phase === '__new__') {
      // Get the new phase name from the form
      const newPhaseNameElement = document.getElementsByName('newPhaseName')[0];
      const newPhaseName = newPhaseNameElement ? newPhaseNameElement.value : '';
      
      if (!newPhaseName) {
        enqueueSnackbar('Please enter a name for the new phase', { variant: 'warning' });
        return;
      }
      
      finalPhase = newPhaseName;
    }
    
    try {
      // Determine the sequence number based on insertion position
      let sequence = 0;
      
      if (afterActivityId) {
        // Find the item we're inserting after
        const afterItem = planItems.find(item => item.id === afterActivityId);
        if (afterItem) {
          // Get all items in the same phase
          const phaseItems = planItems.filter(item => item.PHASE === finalPhase);
          
          // Find items with sequence greater than the afterItem
          const itemsAfter = phaseItems.filter(item => 
            (item.SEQUENCE || 0) > (afterItem.SEQUENCE || 0)
          );
          
          // If inserting in the middle, increment sequence of all items after
          if (itemsAfter.length > 0) {
            for (const item of itemsAfter) {
              await auditService.updateAuditPlan(item.id, {
                ...item,
                SEQUENCE: (item.SEQUENCE || 0) + 1
              });
            }
          }
          
          // Set sequence to one more than the reference item
          sequence = (afterItem.SEQUENCE || 0) + 1;
        } else {
          // If reference not found, add to end
          const phaseItems = planItems.filter(item => item.PHASE === finalPhase);
          sequence = phaseItems.length > 0 
            ? Math.max(...phaseItems.map(item => item.SEQUENCE || 0)) + 1 
            : 0;
        }
      } else {
        // No specific position - add to end of the phase
        const phaseItems = planItems.filter(item => item.PHASE === finalPhase);
        sequence = phaseItems.length > 0 
          ? Math.max(...phaseItems.map(item => item.SEQUENCE || 0)) + 1 
          : 0;
      }
      
      const formData = {
        PHASE: finalPhase,
        ACTIVITY: activityName,
        OWNER: null,
        START_DATE: null,
        END_DATE: null,
        STATUS: 'Not Started',
        AUDIT_ID: id,
        COMPANY_ID: selectedProject.COMPANY_ID?.id || null,
        SEQUENCE: sequence,
        DOCUMENT_TYPE: document_type // Add document type to all new items
      };
      
      // Create new activity
      const response = await auditService.createAuditPlan(formData);
      
      // Refresh data
      const newPlanData = await auditService.fetchAuditPlansByType(id, document_type);
      const sortedPlanData = sortBySequence(newPlanData);
      setPlanItems(sortedPlanData);
      
      // Close the quick add menu
      handleQuickAddClose();
      
      enqueueSnackbar('Activity added successfully', { variant: 'success' });
      
      // If we have a position, scroll to it
      if (response && response.id) {
        setTimeout(() => {
          const element = document.getElementById(`activity-row-${response.id}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('highlight-new-activity');
            setTimeout(() => element.classList.remove('highlight-new-activity'), 2000);
          }
        }, 300);
      }
    } catch (error) {
      console.error('Error adding activity:', error);
      enqueueSnackbar('Failed to add activity', { variant: 'error' });
    }
  };
  
  // Fix the insert activity functionality to properly pass the phase
  const handleInsertActivityClick = (event, phase, afterActivity = null, position = null) => {
    setQuickAddPhase(phase);
    setInsertPhase(phase);
    setInsertAfter(afterActivity);
    setInsertPosition(position);
    setAnchorEl(event.currentTarget);
    setQuickAddOpen(true);
  };

  // Add a function to export the audit plan to Excel
  const exportToExcel = async () => {
    try {
      setExportLoading(true);
      
      // Create a new workbook
      const wb = XLSX.utils.book_new();
      
      // Sort plan items by phase and sequence
      const sortedPlanItems = sortBySequence([...planItems]);
      
      // Group items by phase
      const groupedByPhase = {};
      sortedPlanItems.forEach(item => {
        if (!groupedByPhase[item.PHASE]) {
          groupedByPhase[item.PHASE] = [];
        }
        groupedByPhase[item.PHASE].push(item);
      });
      
      // Create data array for summary worksheet
      const summaryData = [
        // Title row with project info
        [selectedProject?.AUDIT_NAME || 'Audit Plan'],
        ['Project ID', selectedProject?.id || ''],
        ['Period End Date', selectedProject?.PERIOD_END_DATE ? formatDate(selectedProject?.PERIOD_END_DATE) : 'Not set'],
        ['Project Owner', selectedProject?.CREATED_BY || 'Not assigned'],
        [''],  // Empty row
        ['Audit Plan Summary'],
        ['Phase', 'Total Activities', 'Not Started', 'In Progress', 'Completed', 'Past Due'],
      ];
      
      // Add phase summary data
      const phases = Object.keys(groupedByPhase).sort((a, b) => sortPhasesByOrder([a, b])[0] === a ? -1 : 1);
      
      phases.forEach(phase => {
        const phaseActivities = groupedByPhase[phase];
        const notStarted = phaseActivities.filter(item => item.STATUS === 'Not Started').length;
        const inProgress = phaseActivities.filter(item => item.STATUS === 'In Progress').length;
        const completed = phaseActivities.filter(item => item.STATUS === 'Completed').length;
        const pastDue = phaseActivities.filter(item => isPastDue(item) && item.STATUS !== 'Completed').length;
        
        summaryData.push([
          phase,
          phaseActivities.length,
          notStarted,
          inProgress,
          completed,
          pastDue
        ]);
      });
      
      // Add total row
      const totalActivities = planItems.length;
      const totalNotStarted = planItems.filter(item => item.STATUS === 'Not Started').length;
      const totalInProgress = planItems.filter(item => item.STATUS === 'In Progress').length;
      const totalCompleted = planItems.filter(item => item.STATUS === 'Completed').length;
      const totalPastDue = planItems.filter(item => isPastDue(item) && item.STATUS !== 'Completed').length;
      
      summaryData.push([
        'TOTAL',
        totalActivities,
        totalNotStarted,
        totalInProgress,
        totalCompleted,
        totalPastDue
      ]);
      
      // Create a worksheet from the data
      const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
      
      // Set column widths
      const summaryColWidths = [
        { wch: 25 },  // Phase
        { wch: 15 },  // Total Activities
        { wch: 15 },  // Not Started
        { wch: 15 },  // In Progress
        { wch: 15 },  // Completed
        { wch: 15 },  // Past Due
      ];
      
      summaryWs['!cols'] = summaryColWidths;
      
      // Add the summary worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");
      
      // Create detailed worksheet for each phase
      phases.forEach(phase => {
        const phaseActivities = groupedByPhase[phase];
        
        // Create data array
        const phaseData = [
          [phase + ' - Detailed Activities'],
          ['#', 'Activity', 'Owner', 'Start Date', 'End Date', 'Status'],
        ];
        
        // Add activity rows
        phaseActivities.forEach((item, index) => {
          phaseData.push([
            index + 1,
            item.ACTIVITY,
            item.owner_details ? `${item.owner_details.first_name} ${item.owner_details.last_name}` : 'Unassigned',
            item.START_DATE ? formatDate(item.START_DATE) : 'Not set',
            item.END_DATE ? formatDate(item.END_DATE) : 'Not set',
            isPastDue(item) ? 'Past Due' : item.STATUS
          ]);
        });
        
        // Create a worksheet from the data
        const phaseWs = XLSX.utils.aoa_to_sheet(phaseData);
        
        // Set column widths
        const phaseColWidths = [
          { wch: 5 },    // #
          { wch: 40 },   // Activity
          { wch: 20 },   // Owner
          { wch: 15 },   // Start Date
          { wch: 15 },   // End Date
          { wch: 15 },   // Status
        ];
        
        phaseWs['!cols'] = phaseColWidths;
        
        // Add the phase worksheet to the workbook
        XLSX.utils.book_append_sheet(wb, phaseWs, phase.substring(0, 30)); // Excel limits sheet names to 31 chars
      });
      
      // Create a timeline view worksheet
      const timelineData = [
        ['Timeline View - ' + selectedProject?.AUDIT_NAME],
        [''],
        ['Phase', 'Activity', 'Owner', 'Status', 'Start Date', 'End Date', 'Duration (Days)']
      ];
      
      // Add activities with timeline info
      sortedPlanItems.forEach(item => {
        const startDate = item.START_DATE ? new Date(item.START_DATE) : null;
        const endDate = item.END_DATE ? new Date(item.END_DATE) : null;
        
        // Calculate duration in days
        const duration = (startDate && endDate) 
          ? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1 
          : 'N/A';
        
        timelineData.push([
          item.PHASE,
          item.ACTIVITY,
          item.owner_details ? `${item.owner_details.first_name} ${item.owner_details.last_name}` : 'Unassigned',
          isPastDue(item) ? 'Past Due' : item.STATUS,
          startDate ? formatDate(item.START_DATE) : 'Not set',
          endDate ? formatDate(item.END_DATE) : 'Not set',
          duration
        ]);
      });
      
      // Create a worksheet from the data
      const timelineWs = XLSX.utils.aoa_to_sheet(timelineData);
      
      // Set column widths
      const timelineColWidths = [
        { wch: 20 },   // Phase
        { wch: 40 },   // Activity
        { wch: 20 },   // Owner
        { wch: 15 },   // Status
        { wch: 15 },   // Start Date
        { wch: 15 },   // End Date
        { wch: 15 },   // Duration
      ];
      
      timelineWs['!cols'] = timelineColWidths;
      
      // Add the timeline worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, timelineWs, "Timeline");
      
      // Create a comprehensive Gantt chart worksheet
      // First, determine the overall date range for all activities
      let minDate = null;
      let maxDate = null;
      
      sortedPlanItems.forEach(item => {
        if (item.START_DATE) {
          const startDate = new Date(item.START_DATE);
          if (!minDate || startDate < minDate) {
            minDate = new Date(startDate);
          }
        }
        
        if (item.END_DATE) {
          const endDate = new Date(item.END_DATE);
          if (!maxDate || endDate > maxDate) {
            maxDate = new Date(endDate);
          }
        }
      });
      
      // Default dates if none are set
      if (!minDate) minDate = new Date();
      if (!maxDate) maxDate = new Date(minDate);
      maxDate.setMonth(maxDate.getMonth() + 3); // At least 3 months range
      
      // Ensure the date range makes sense and is at least 7 days
      if (maxDate - minDate < 7 * 24 * 60 * 60 * 1000) {
        maxDate = new Date(minDate);
        maxDate.setDate(maxDate.getDate() + 30); // Set to at least 30 days
      }
      
      // Adjust min and max dates to nice round dates (beginning and end of months)
      minDate.setDate(1);
      maxDate.setDate(1);
      maxDate.setMonth(maxDate.getMonth() + 1);
      maxDate.setDate(0); // Last day of month
      
      // Create an array of all dates in the range (for column headers)
      const allDates = [];
      const currentDate = new Date(minDate);
      
      while (currentDate <= maxDate) {
        allDates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      // Group activities by phase for the Gantt chart
      // Create headers: Phase + Activity + a column for each date
      const ganttHeaders = ['Phase', 'Activity'];
      const dateColumns = allDates.map(date => format(date, 'MMM d'));
      ganttHeaders.push(...dateColumns);
      
      // Initialize Gantt data with headers
      const ganttData = [
        [`${selectedProject?.AUDIT_NAME || 'Audit Plan'} - Gantt Chart`],
        ['', ''], // Placeholder for date range
        ganttHeaders
      ];
      
      // Set date range in the header
      ganttData[1][0] = `Date Range: ${format(minDate, 'MMM d, yyyy')} to ${format(maxDate, 'MMM d, yyyy')}`;
      
      // Add activities to Gantt data, grouped by phase
      phases.forEach(phase => {
        // Add phase header row
        ganttData.push([phase, '']);
        
        // Add each activity in this phase
        const phaseActivities = groupedByPhase[phase];
        
        phaseActivities.forEach(activity => {
          const activityRow = [
            '', // Phase (empty because we have phase headers)
            activity.ACTIVITY
          ];
          
          // Add cells for each date - filled if the activity spans this date
          const activityStartDate = activity.START_DATE ? new Date(activity.START_DATE) : null;
          const activityEndDate = activity.END_DATE ? new Date(activity.END_DATE) : null;
          
          // If dates are not set, leave all cells empty
          if (!activityStartDate || !activityEndDate) {
            // Fill with empty strings for each date
            activityRow.push(...Array(dateColumns.length).fill(''));
          } else {
            // For each date, check if activity is active on that date
            allDates.forEach(date => {
              const currentDateStr = format(date, 'yyyy-MM-dd');
              const startDateStr = format(activityStartDate, 'yyyy-MM-dd');
              const endDateStr = format(activityEndDate, 'yyyy-MM-dd');
              
              if (currentDateStr >= startDateStr && currentDateStr <= endDateStr) {
                // Date is within activity span - mark with status code
                let statusIndicator = '';
                if (isPastDue(activity)) {
                  statusIndicator = 'P'; // Past Due
                } else if (activity.STATUS === 'Completed') {
                  statusIndicator = 'C'; // Completed
                } else if (activity.STATUS === 'In Progress') {
                  statusIndicator = 'I'; // In Progress
                } else {
                  statusIndicator = 'N'; // Not Started
                }
                activityRow.push(statusIndicator);
              } else {
                // Date is outside activity span
                activityRow.push('');
              }
            });
          }
          
          ganttData.push(activityRow);
        });
        
        // Add empty row after each phase for better readability
        ganttData.push(['', '']);
      });
      
      // Create the Gantt chart worksheet
      const ganttWs = XLSX.utils.aoa_to_sheet(ganttData);
      
      // Set column widths for Gantt chart
      const ganttColWidths = [
        { wch: 20 },  // Phase
        { wch: 40 },  // Activity
        ...Array(dateColumns.length).fill({ wch: 5 })  // Date columns
      ];
      
      ganttWs['!cols'] = ganttColWidths;
      
      // Add the Gantt chart worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ganttWs, "Gantt Chart");
      
      // Add a second enhanced visual Gantt chart that looks better
      const enhancedGanttData = [
        [`${selectedProject?.AUDIT_NAME || 'Audit Plan'} - Enhanced Gantt Chart`],
        [''], // Empty row
        ['ID', 'Phase', 'Activity', 'Owner', 'Status', 'Start Date', 'End Date', 'Duration (Days)', 'Percent Complete', 'Visual Timeline']
      ];
      
      // Group months for the visual timeline header
      const months = [];
      let currentMonth = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
      
      while (currentMonth <= maxDate) {
        months.push({
          month: format(currentMonth, 'MMM yyyy'),
          days: new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate()
        });
        currentMonth.setMonth(currentMonth.getMonth() + 1);
      }
      
      // Calculate total days in timeline for visual scaling
      const totalDays = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) + 1;
      
      // Add month headers for visual timeline
      const monthHeaderRow = ['', '', '', '', '', '', '', '', ''];
      
      months.forEach(month => {
        monthHeaderRow.push(month.month);
      });
      
      enhancedGanttData.push(monthHeaderRow);
      
      // Add day headers (1-31) for each month
      const dayHeaderRow = ['', '', '', '', '', '', '', '', ''];
      
      months.forEach(month => {
        for (let day = 1; day <= month.days; day++) {
          // Only add every 7th day to avoid too many columns
          if (day % 7 === 1 || day === month.days) {
            dayHeaderRow.push(day.toString());
          } else {
            dayHeaderRow.push('');
          }
        }
      });
      
      enhancedGanttData.push(dayHeaderRow);
      
      // Add activities to enhanced Gantt data
      let activityId = 1;
      
      phases.forEach(phase => {
        const phaseActivities = groupedByPhase[phase];
        
        phaseActivities.forEach(activity => {
          const startDate = activity.START_DATE ? new Date(activity.START_DATE) : null;
          const endDate = activity.END_DATE ? new Date(activity.END_DATE) : null;
          
          // Calculate duration in days
          const duration = (startDate && endDate) 
            ? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1 
            : '';
          
          // Calculate percent complete based on status
          let percentComplete = '';
          if (activity.STATUS === 'Completed') {
            percentComplete = '100%';
          } else if (activity.STATUS === 'In Progress') {
            percentComplete = '50%';
          } else if (activity.STATUS === 'Not Started') {
            percentComplete = '0%';
          } else if (isPastDue(activity)) {
            percentComplete = '25%';
          }
          
          // Create the activity row
          const activityRow = [
            activityId++,
            phase,
            activity.ACTIVITY,
            activity.owner_details ? `${activity.owner_details.first_name} ${activity.owner_details.last_name}` : 'Unassigned',
            isPastDue(activity) ? 'Past Due' : activity.STATUS,
            startDate ? format(startDate, 'MMM d, yyyy') : '',
            endDate ? format(endDate, 'MMM d, yyyy') : '',
            duration,
            percentComplete
          ];
          
          // Add visual timeline representation
          if (startDate && endDate) {
            // Calculate the visual representation for the timeline
            // This will be a text representation using characters to show the timeline
            // For Excel, we'll use "████████" to represent the activity duration
            
            // Calculate start position in days from minDate
            const startOffset = Math.floor((startDate - minDate) / (1000 * 60 * 60 * 24));
            
            // Calculate duration
            const activityDuration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
            
            // Create visual bar based on status
            let visualBar = '';
            if (isPastDue(activity)) {
              visualBar = '!'.repeat(activityDuration); // Past Due
            } else if (activity.STATUS === 'Completed') {
              visualBar = '█'.repeat(activityDuration); // Completed
            } else if (activity.STATUS === 'In Progress') {
              visualBar = '▓'.repeat(activityDuration); // In Progress
            } else {
              visualBar = '▒'.repeat(activityDuration); // Not Started
            }
            
            activityRow.push(visualBar);
          } else {
            activityRow.push('');
          }
          
          enhancedGanttData.push(activityRow);
        });
        
        // Add empty row after each phase
        enhancedGanttData.push(Array(10).fill(''));
      });
      
      // Create the enhanced Gantt chart worksheet
      const enhancedGanttWs = XLSX.utils.aoa_to_sheet(enhancedGanttData);
      
      // Set column widths for enhanced Gantt chart
      const enhancedGanttColWidths = [
        { wch: 5 },   // ID
        { wch: 15 },  // Phase
        { wch: 40 },  // Activity
        { wch: 20 },  // Owner
        { wch: 12 },  // Status
        { wch: 15 },  // Start Date
        { wch: 15 },  // End Date
        { wch: 15 },  // Duration
        { wch: 15 },  // % Complete
        { wch: 150 }  // Visual Timeline
      ];
      
      enhancedGanttWs['!cols'] = enhancedGanttColWidths;
      
      // Add the enhanced Gantt chart worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, enhancedGanttWs, "Enhanced Gantt");
      
      // Apply formatting to the workbook
      const formattedWb = formatExcelData(wb);
      
      // Generate Excel file
      const excelBuffer = XLSX.write(formattedWb, { bookType: 'xlsx', type: 'array' });
      
      // Create a Blob from the buffer
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Create download link
      const fileName = `${selectedProject?.AUDIT_NAME || 'Audit'}_Plan_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setExportLoading(false);
      enqueueSnackbar('Audit plan exported successfully with Gantt charts', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting audit plan:', error);
      enqueueSnackbar('Failed to export audit plan', { variant: 'error' });
      setExportLoading(false);
    }
  };
  
  // Update Box component in the Header to include the export button
  // Look for this section:
  //   <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 4 }}>
  
  // Modify mainContent to include the export button in the header
  const mainContent = (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ResponsiveContainer>
        <mui.Box sx={{ 
          padding: { xs: '16px', sm: '24px' },
          maxWidth: '1600px',
          margin: '0 auto'
        }}>
          {/* Breadcrumbs */}
          <mui.Breadcrumbs 
            aria-label="breadcrumb"
            sx={{
              '& .MuiLink-root': {
                display: 'flex',
                alignItems: 'center',
                color: 'text.secondary',
                fontSize: '0.875rem',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: 'primary.main'
                }
              }
            }}
          >
            <mui.Link underline="hover" color="inherit" href="/dashboard">
              <Icons.Home sx={{ mr: 0.5, fontSize: 16 }} />
              Home
            </mui.Link>
            <mui.Link underline="hover" color="inherit" href="/Audit/Projects">
              Audit Projects
            </mui.Link>
            <mui.Link underline="hover" color="inherit" href={`/audit/projects/${id}`}>
              Project Settings
            </mui.Link>
            <mui.Link underline="hover" color="inherit" href={`/audit/projects/${id}/planning`}>
              Planning Documents
            </mui.Link>
            <mui.Typography color="text.primary">{documentInfo.name}</mui.Typography>
          </mui.Breadcrumbs>

          {/* Header */}
          <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 4 }}>
            <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
              <mui.Avatar 
                sx={{ 
                  backgroundColor: `${documentInfo.color}15`, 
                  color: documentInfo.color,
                  width: 40, 
                  height: 40, 
                  mr: 2 
                }}
              >
                {document_type === 'IT Audit' ? <Icons.Computer /> :
                 document_type === 'Financial Audit' ? <Icons.AccountBalance /> :
                 document_type === 'Operational Audit' ? <Icons.Settings /> :
                 document_type === 'SOX' ? <Icons.Gavel /> :
                 document_type === 'SOC' ? <Icons.Security /> :
                 document_type === 'Custom' ? <Icons.Edit /> :
                 <Icons.Assignment />}
              </mui.Avatar>
              <mui.Box>
              <mui.Typography variant="h4" component="h1" fontWeight="600">
                  {documentInfo.name}
                </mui.Typography>
                <mui.Typography variant="subtitle1" color="text.secondary">
                  Audit Planning Details
              </mui.Typography>
              </mui.Box>
            </mui.Box>
            <mui.Box sx={{ display: 'flex', gap: 2 }}>
              <mui.Button
                variant="outlined"
                color="primary"
                startIcon={<Icons.ArrowBack />}
                onClick={handleBackToDocuments}
                sx={{ borderRadius: '8px' }}
              >
                Back to Documents
              </mui.Button>
              <mui.Button
                variant="outlined"
                color="primary"
                startIcon={<Icons.FileDownload />}
                onClick={exportToExcel}
                disabled={exportLoading || planItems.length === 0}
                sx={{ borderRadius: '8px' }}
              >
                {exportLoading ? 'Exporting...' : 'Export to Excel'}
              </mui.Button>
              <mui.Button
                variant="outlined"
                color="error"
                startIcon={<Icons.DeleteSweep />}
                onClick={() => setDeletePlanDialogOpen(true)}
                sx={{ borderRadius: '8px' }}
                disabled={planItems.length === 0}
              >
                Delete Plan
              </mui.Button>
              <mui.Button
                variant="contained"
                color="primary"
                startIcon={<Icons.Add />}
                onClick={() => {
                  resetForm();
                  setFormOpen(true);
                }}
                sx={{ borderRadius: '8px' }}
              >
                Add Plan Item
              </mui.Button>
            </mui.Box>
          </mui.Box>

          {/* Activity Scheduling Dialog */}
          <mui.Dialog
            open={scheduleDialogOpen}
            onClose={() => {
              setScheduleDialogOpen(false);
              setSelectedActivity(null);
              setSelectionStart(null);
              setSelectionEnd(null);
            }}
            maxWidth="sm"
            fullWidth
          >
            <mui.DialogTitle sx={{ fontWeight: 600 }}>
              Schedule Activity
            </mui.DialogTitle>
            <mui.DialogContent>
              {selectedActivity && (
                <>
                  <mui.Box sx={{ mb: 3 }}>
                    <mui.Typography variant="subtitle1" fontWeight="600" color="primary.main" gutterBottom>
                      {selectedActivity.PHASE}
                    </mui.Typography>
                    <mui.Typography variant="body1" gutterBottom>
                      {selectedActivity.ACTIVITY}
                    </mui.Typography>
                  </mui.Box>
                  
                  <mui.Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      mb: 3,
                      bgcolor: 'info.lighter',
                      color: 'info.dark',
                      border: '1px solid',
                      borderColor: 'info.light',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Icons.Info sx={{ fontSize: 20, mr: 1 }} />
                    <mui.Typography variant="body2">
                      You've selected a timespan of {
                        scheduleStartDate && scheduleEndDate 
                        ? Math.ceil((scheduleEndDate - scheduleStartDate) / (1000 * 60 * 60 * 24)) + 1 
                        : '0'
                      } days across {
                        selectionStart && selectionEnd
                        ? Math.ceil((selectionEnd.monthIndex - selectionStart.monthIndex) * 4 + 
                            (selectionEnd.weekIndex - selectionStart.weekIndex + 1)) 
                        : '0'
                      } weeks.
                    </mui.Typography>
                  </mui.Paper>
                  
                  <mui.Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    You can fine-tune the dates below or return to the timeline to adjust your selection by drag-selecting multiple weeks.
                  </mui.Typography>
                  
                  <mui.Grid container spacing={3}>
                    <mui.Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Start Date"
                        value={scheduleStartDate}
                        onChange={setScheduleStartDate}
                        renderInput={(params) => 
                          <mui.TextField {...params} fullWidth required />
                        }
                        inputFormat="MMM d, yyyy"
                      />
                    </mui.Grid>
                    
                    <mui.Grid item xs={12} sm={6}>
                      <DatePicker
                        label="End Date"
                        value={scheduleEndDate}
                        onChange={setScheduleEndDate}
                        renderInput={(params) => 
                          <mui.TextField {...params} fullWidth required />
                        }
                        inputFormat="MMM d, yyyy"
                        minDate={scheduleStartDate || undefined}
                      />
                    </mui.Grid>
                  </mui.Grid>
                </>
              )}
            </mui.DialogContent>
            <mui.DialogActions sx={{ px: 3, pb: 3 }}>
              <mui.Button 
                onClick={() => setScheduleDialogOpen(false)}
                variant="outlined"
                sx={{ borderRadius: '8px' }}
              >
                Cancel
              </mui.Button>
              <mui.Button 
                onClick={handleSaveSchedule}
                variant="contained"
                color="primary"
                sx={{ borderRadius: '8px' }}
                startIcon={<Icons.Event />}
                disabled={!scheduleStartDate || !scheduleEndDate}
              >
                Schedule Activity
              </mui.Button>
            </mui.DialogActions>
          </mui.Dialog>

          {loading ? (
            <mui.Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
              <mui.CircularProgress size={40} />
              <mui.Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
                Loading {document_type} planning document...
              </mui.Typography>
            </mui.Box>
          ) : (
            <>
              {/* Project Info Card */}
              {selectedProject && (
                <mui.Paper sx={{ 
                  p: 3, 
                  mb: 4, 
                  borderRadius: 2,
                  backgroundColor: '#f0f9ff',
                  border: '1px solid',
                  borderColor: 'rgba(148, 163, 184, 0.1)'
                }}>
                  <mui.Grid container spacing={2}>
                    <mui.Grid item xs={12} md={6}>
                      <mui.Typography variant="h6" fontWeight="600" mb={1}>
                        {selectedProject.AUDIT_NAME}
                      </mui.Typography>
                      <mui.Typography variant="body2" color="text.secondary" paragraph>
                        {selectedProject.AUDIT_DESCRIPTION}
                      </mui.Typography>
                      <mui.Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                        <mui.Chip
                          size="small"
                          label={selectedProject.AUDIT_TYPE || 'N/A'}
                          sx={{ 
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            color: 'primary.main',
                            fontWeight: 500,
                            mr: 1
                          }}
                        />
                        <mui.Chip
                          size="small"
                          label={selectedProject.STATUS || 'N/A'}
                          sx={{ 
                            backgroundColor: 'rgba(25, 118, 210, 0.08)',
                            color: 'primary.main',
                            fontWeight: 500
                          }}
                        />
                      </mui.Box>
                    </mui.Grid>
                    <mui.Grid item xs={12} md={6}>
                      <mui.Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mt: { xs: 2, md: 0 } }}>
                        <mui.Box>
                          <mui.Typography variant="subtitle2" color="text.secondary">
                            End Date
                          </mui.Typography>
                          <mui.Typography variant="body1" fontWeight="500">
                            {selectedProject.PERIOD_END_DATE ? formatDate(selectedProject.PERIOD_END_DATE) : 'Not set'}
                          </mui.Typography>
                          
                          <mui.Typography variant="subtitle2" color="text.secondary" mt={2}>
                            Project Owner
                          </mui.Typography>
                          <mui.Typography variant="body1" fontWeight="500">
                            {selectedProject.CREATED_BY || 'Not assigned'}
                          </mui.Typography>
                        </mui.Box>
                      </mui.Box>
                    </mui.Grid>
                  </mui.Grid>
                </mui.Paper>
              )}

              {/* Custom Gantt Chart */}
              {planItems.length > 0 && (
                <mui.Paper sx={{ 
                  p: 3, 
                  mb: 4, 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'rgba(148, 163, 184, 0.1)',
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
                  backgroundImage: 'linear-gradient(to bottom, #ffffff, #fcfcff)'
                }}>
                  <mui.Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 4
                  }}>
                    <mui.Typography variant="h6" fontWeight="600" sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      '&::before': {
                        content: '""',
                        display: 'block',
                        width: '4px',
                        height: '24px',
                        backgroundColor: 'primary.main',
                        borderRadius: '4px',
                        marginRight: '12px'
                      }
                    }}>
                      Timeline View
                    </mui.Typography>
                    
                    <mui.Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      borderRadius: '8px',
                      padding: '4px',
                    }}>
                      <mui.Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<Icons.NoteAdd />}
                        onClick={(e) => {
                          // Get the first phase if available, otherwise leave empty
                          const firstPhase = Array.from(new Set(planItems.map(item => item.PHASE)))
                            .sort((a, b) => sortPhasesByOrder([a, b])[0] === a ? -1 : 1)[0] || '';
                          
                          handleInsertActivityClick(e, firstPhase);
                        }}
                        sx={{
                          borderRadius: '6px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                          border: '1px solid rgba(0,0,0,0.08)',
                          mr: 2,
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)'
                          }
                        }}
                      >
                        Quick Add Activity
                      </mui.Button>
                    
                      <mui.Button
                        startIcon={<Icons.ChevronLeft />}
                        onClick={handlePreviousMonths}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: '6px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                          border: '1px solid rgba(0,0,0,0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)'
                          }
                        }}
                      >
                        Previous
                      </mui.Button>
                      
                      <mui.Button
                        variant="outlined"
                        size="small"
                        onClick={() => setDatePickerOpen(true)}
                        startIcon={<Icons.CalendarMonth />}
                        sx={{
                          borderRadius: '6px',
                          minWidth: '120px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                          border: '1px solid rgba(0,0,0,0.08)',
                          fontWeight: 600,
                          bgcolor: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)'
                          }
                        }}
                      >
                        {format(timelineStartDate, 'MMM yyyy')}
                      </mui.Button>
                      
                      <mui.Dialog open={datePickerOpen} onClose={() => setDatePickerOpen(false)}>
                        <mui.DialogTitle sx={{ fontWeight: 600 }}>Jump to Month</mui.DialogTitle>
                        <mui.DialogContent>
                          <DatePicker
                            label="Select Start Month"
                            value={jumpToDate || timelineStartDate}
                            onChange={setJumpToDate}
                            views={['year', 'month']}
                            openTo="month"
                            renderInput={(params) => 
                              <mui.TextField {...params} fullWidth />
                            }
                          />
                        </mui.DialogContent>
                        <mui.DialogActions>
                          <mui.Button onClick={() => setDatePickerOpen(false)}>Cancel</mui.Button>
                          <mui.Button onClick={handleJumpToDate} variant="contained">
                            Jump to Date
                          </mui.Button>
                        </mui.DialogActions>
                      </mui.Dialog>
                      
                      <mui.Button
                        endIcon={<Icons.ChevronRight />}
                        onClick={handleNextMonths}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: '6px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
                          border: '1px solid rgba(0,0,0,0.08)',
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.08)'
                          }
                        }}
                      >
                        Next
                      </mui.Button>
                      
                      <mui.Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                      
                      <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <mui.Typography variant="caption" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                          Zoom:
                        </mui.Typography>
                        <mui.ToggleButtonGroup
                          value={zoomLevel}
                          exclusive
                          onChange={(e, newZoom) => {
                            if (newZoom !== null) {
                              setZoomLevel(newZoom);
                              // Auto-set extended view when in compact mode
                              if (newZoom === 'compact') {
                                setExtendedView(true);
                              }
                            }
                          }}
                          size="small"
                          sx={{ 
                            '& .MuiToggleButtonGroup-grouped': {
                              border: '1px solid rgba(0,0,0,0.08) !important',
                              px: 1.5,
                              py: 0.5,
                              fontSize: '0.7rem',
                              fontWeight: 500
                            },
                            '& .MuiToggleButton-root.Mui-selected': {
                              '&[value="compact"]': {
                                backgroundColor: 'rgba(14, 165, 233, 0.1)',
                                color: 'rgb(14, 165, 233)',
                                borderColor: 'rgba(14, 165, 233, 0.2) !important',
                              },
                              '&[value="normal"]': {
                                backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                color: 'rgb(25, 118, 210)',
                                borderColor: 'rgba(25, 118, 210, 0.2) !important',
                              },
                              '&[value="detailed"]': {
                                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                color: 'rgb(34, 197, 94)',
                                borderColor: 'rgba(34, 197, 94, 0.2) !important',
                              }
                            }
                          }}
                        >
                          <mui.ToggleButton value="compact">
                            <Icons.ZoomOut fontSize="small" sx={{ mr: 0.5, fontSize: '0.95rem' }} />
                            Compact
                          </mui.ToggleButton>
                          <mui.ToggleButton value="normal">
                            <Icons.ZoomOutMap fontSize="small" sx={{ mr: 0.5, fontSize: '0.95rem' }} />
                            Normal
                          </mui.ToggleButton>
                          <mui.ToggleButton value="detailed">
                            <Icons.ZoomIn fontSize="small" sx={{ mr: 0.5, fontSize: '0.95rem' }} />
                            Detailed
                          </mui.ToggleButton>
                        </mui.ToggleButtonGroup>
                      </mui.Box>
                      
                      <mui.FormControlLabel
                        control={
                          <mui.Switch 
                            checked={extendedView}
                            onChange={() => setExtendedView(!extendedView)}
                            size="small"
                            color="primary"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: 'primary.main',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: 'primary.light',
                              },
                            }}
                          />
                        }
                        label={
                          <mui.Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {extendedView ? '6 Months View' : '3 Months View'}
                          </mui.Typography>
                        }
                        sx={{ ml: 1 }}
                      />
                    </mui.Box>
                  </mui.Box>
                  
                  <mui.Box sx={{ 
                    overflowX: 'auto',
                    boxShadow: 'inset 0 0 20px rgba(0,0,0,0.02)',
                    borderRadius: 1,
                    pb: 1,
                    width: '100%',
                    maxWidth: '100%',
                    position: 'relative', // Add relative positioning for the container
                    '&::-webkit-scrollbar': {
                      height: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(0,0,0,0.1)',
                      borderRadius: '10px',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.15)',
                      },
                    },
                  }}>
                    <mui.Table sx={{ 
                      minWidth: zoomLevel === 'compact' ? 700 : 1500, 
                      width: '100%',
                      tableLayout: zoomLevel === 'compact' ? 'fixed' : 'auto',
                      borderCollapse: 'separate',
                      borderSpacing: 0,
                      overflow: 'visible',
                      '& .MuiTableCell-root': {
                        borderColor: 'rgba(224, 224, 224, 0.5)',
                        ...(zoomLevel === 'compact' && {
                          padding: '2px'
                        })
                      },
                      // Style for the sticky first column cells
                      '& .sticky-column': {
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        backgroundColor: '#ffffff',
                        boxShadow: '5px 0 8px rgba(0,0,0,0.06)',
                        // Add transition for smoother experience when hovering rows
                        transition: 'background-color 0.2s ease',
                        clipPath: 'inset(0px -15px 0px 0px)' // Clip the shadow to the right side only
                      },
                      // Style for header cells
                      '& .sticky-header': {
                        zIndex: 3, // Higher z-index than regular cells
                        backgroundColor: '#fafbff'
                      },
                      // Style for alternating row colors
                      '& .sticky-column-alt': {
                        backgroundColor: 'rgba(250, 251, 255, 0.9)'
                      },
                      // Hover state for the sticky column to match row hover
                      '& tr:hover .sticky-column': {
                        backgroundColor: 'rgba(240, 249, 255, 0.95)'
                      }
                    }}>
                      <mui.TableHead>
                        {/* Month headers */}
                        <mui.TableRow>
                          <mui.TableCell 
                            className="sticky-column sticky-header"
                            sx={{ 
                              width: zoomLevel === 'compact' ? 220 : (zoomLevel === 'detailed' ? 500 : 400), 
                              minWidth: zoomLevel === 'compact' ? 220 : (zoomLevel === 'detailed' ? 500 : 400),
                              borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                              borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                              fontWeight: 'bold',
                              color: 'text.primary',
                              py: zoomLevel === 'compact' ? 0.75 : 1
                            }}
                          >
                            Phase / Activity
                          </mui.TableCell>
                          {getTimelineMonths(timelineStartDate, extendedView).map((month, index) => (
                            <mui.TableCell 
                              key={`${month.month}-${index}`}
                              align="center"
                              colSpan={4}
                              sx={{ 
                                borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                                borderRight: index < (extendedView ? 5 : 2) ? '1px solid rgba(224, 224, 224, 0.5)' : 'none',
                                backgroundImage: 'linear-gradient(to bottom, #f8faff, #f1f5fd)',
                                fontWeight: 600,
                                color: 'primary.dark',
                                py: zoomLevel === 'compact' ? 0.3 : 0.75,
                                fontSize: zoomLevel === 'compact' ? '0.7rem' : '0.8rem',
                                px: zoomLevel === 'compact' ? 1 : 2,
                                height: zoomLevel === 'compact' ? 24 : 32
                              }}
                            >
                              {month.month.substring(0, 3)}
                            </mui.TableCell>
                          ))}
                        </mui.TableRow>
                        
                        {/* Week headers - make the cells taller and actually display the week numbers */}
                        <mui.TableRow>
                          <mui.TableCell 
                            className="sticky-column sticky-header"
                            sx={{ 
                              width: zoomLevel === 'compact' ? 220 : (zoomLevel === 'detailed' ? 500 : 400),
                              minWidth: zoomLevel === 'compact' ? 220 : (zoomLevel === 'detailed' ? 500 : 400),
                              borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                              borderBottom: '1px solid rgba(224, 224, 224, 0.5)'
                            }}
                          />
                          {getTimelineMonths(timelineStartDate, extendedView).map((month, monthIndex) => (
                            month.weeks.map((week, weekIndex) => (
                              <mui.TableCell 
                                key={`${week}-${monthIndex}-${weekIndex}`}
                                align="center"
                                sx={{ 
                                  borderRight: weekIndex < 3 || monthIndex < (extendedView ? 5 : 2) ? '1px solid rgba(224, 224, 224, 0.5)' : 'none',
                                  borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                                  minWidth: zoomLevel === 'compact' ? 38 : (zoomLevel === 'detailed' ? 160 : 120),
                                  maxWidth: zoomLevel === 'compact' ? 38 : (zoomLevel === 'detailed' ? 240 : 180),
                                  width: zoomLevel === 'compact' ? 38 : 'auto',
                                  padding: zoomLevel === 'compact' ? '4px 0' : '4px 8px',
                                  backgroundColor: weekIndex % 2 === 0 ? 'rgba(249, 250, 255, 0.8)' : 'rgba(245, 247, 255, 0.5)',
                                  height: zoomLevel === 'compact' ? 24 : 32
                                }}
                              >
                                <mui.Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                    fontSize: zoomLevel === 'compact' ? '0.6rem' : '0.7rem'
                                  }}
                                >
                                  {week}
                                </mui.Typography>
                              </mui.TableCell>
                            ))
                          ))}
                        </mui.TableRow>
                      </mui.TableHead>
                      
                      <mui.TableBody>
                        {/* Group plans by phase - when using batch view */}
                        {zoomLevel === 'compact' ? (
                          Array.from(new Set(planItems.map(item => item.PHASE)))
                            .sort((a, b) => sortPhasesByOrder([a, b])[0] === a ? -1 : 1)
                            .map((phase, phaseIndex) => {
                            // Get all activities for this phase
                            const phaseActivities = planItems.filter(item => item.PHASE === phase);
                            
                            return (
                              <mui.TableRow 
                                key={`phase-${phaseIndex}`}
                                sx={{ 
                                  backgroundColor: phaseIndex % 2 === 0 ? 'rgba(255, 255, 255, 0.8)' : 'rgba(250, 251, 255, 0.5)',
                                  '&:hover': { 
                                    backgroundColor: 'rgba(240, 249, 255, 0.7)'
                                  }
                                }}
                              >
                                <mui.TableCell 
                                  className={`sticky-column ${phaseIndex % 2 !== 0 ? 'sticky-column-alt' : ''}`}
                                  sx={{ 
                                    width: zoomLevel === 'compact' ? 220 : (zoomLevel === 'detailed' ? 500 : 400),
                                    minWidth: zoomLevel === 'compact' ? 220 : (zoomLevel === 'detailed' ? 500 : 400),
                                    borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                                    borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                                    fontWeight: 600,
                                    py: zoomLevel === 'compact' ? 0.5 : 1.5
                                  }}
                                >
                                  <mui.Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                  }}>
                                    <mui.Typography 
                                      variant={zoomLevel === 'compact' ? 'body2' : 'subtitle2'} 
                                      sx={{ 
                                        color: 'primary.main', 
                                        fontWeight: 600, 
                                        display: 'flex',
                                        alignItems: 'center'
                                      }}
                                    >
                                      <mui.Box 
                                        component="span" 
                                        sx={{ 
                                          width: zoomLevel === 'compact' ? 6 : 8, 
                                          height: zoomLevel === 'compact' ? 6 : 8, 
                                          borderRadius: '50%', 
                                          bgcolor: 'primary.main', 
                                          display: 'inline-block',
                                          mr: 1
                                        }}
                                      />
                                      {phase}
                                      <mui.Typography 
                                        variant="caption" 
                                        component="span" 
                                        sx={{ 
                                          ml: 1,
                                          color: 'text.secondary',
                                          fontWeight: 400
                                        }}
                                      >
                                        ({phaseActivities.length} {phaseActivities.length === 1 ? 'activity' : 'activities'})
                                      </mui.Typography>
                                    </mui.Typography>
                                    
                                    {/* Add Quick Add Button */}
                                    <mui.Tooltip title="Add new activity to this phase">
                                      <mui.IconButton
                                        size="small"
                                        onClick={(e) => handleQuickAddClick(e, phase)}
                                        sx={{ color: 'primary.main' }}
                                      >
                                        <AddCircleOutline fontSize="small" />
                                      </mui.IconButton>
                                    </mui.Tooltip>
                                  </mui.Box>
                                </mui.TableCell>
                                
                                {/* Generate cells for each week of each month */}
                                {getTimelineMonths(timelineStartDate, extendedView).map((month, monthIndex) => (
                                  month.weeks.map((week, weekIndex) => {
                                    // Find all activities that fall in this week
                                    const activitiesInWeek = phaseActivities.filter(activity => 
                                      isActivityInWeek(activity, monthIndex, weekIndex, getTimelineMonths(timelineStartDate, extendedView))
                                    );
                                    
                                    // Check if any of the activities in this week are past due
                                    const hasPastDueActivities = activitiesInWeek.some(activity => isPastDue(activity));
                                    
                                    return (
                                      <mui.TableCell 
                                        key={`${phase}-${month.month}-${week}`}
                                        align="center"
                                        data-month-index={monthIndex}
                                        data-week-index={weekIndex}
                                        onMouseDown={() => {
                                          // Only trigger scheduling for unscheduled activities
                                          if (activity && !activity.START_DATE && !activity.END_DATE) {
                                            handleMouseDown(phase, activity, monthIndex, weekIndex);
                                          }
                                          // For scheduled activities, the onClick handler in the activity box
                                          // will manage status changes
                                        }}
                                        sx={{ 
                                          borderRight: weekIndex < 3 || monthIndex < (extendedView ? 5 : 2) ? '1px solid rgba(224, 224, 224, 0.5)' : 'none',
                                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                                          transition: 'all 0.3s ease',
                                          position: 'relative',
                                          height: zoomLevel === 'compact' ? 20 : zoomLevel === 'detailed' ? 70 : 40,
                                          padding: zoomLevel === 'compact' ? 0 : undefined,
                                          minWidth: zoomLevel === 'compact' ? 36 : (zoomLevel === 'detailed' ? 160 : 120),
                                          width: zoomLevel === 'compact' ? 36 : 'auto',
                                          maxWidth: zoomLevel === 'compact' ? 36 : (zoomLevel === 'detailed' ? 240 : 180),
                                          cursor: 'pointer',
                                          backgroundColor: isCellSelected(monthIndex, weekIndex, phaseActivities[0]?.id) ? 'rgba(59, 130, 246, 0.15)' : 
                                                         (activitiesInWeek.length === 0 ? 'transparent' : 
                                                         hasPastDueActivities ? 'rgba(254, 226, 226, 0.15)' : 'transparent')
                                        }}
                                      >
                                        {activitiesInWeek.length > 0 && (
                                          <mui.Box sx={{ 
                                            position: 'absolute',
                                            top: '4px',
                                            left: '4px',
                                            right: '4px',
                                            bottom: '4px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '4px',
                                            bgcolor: hasPastDueActivities ? 'error.lighter' : 'primary.lighter',
                                            color: hasPastDueActivities ? 'error.dark' : 'primary.dark',
                                            border: '1px solid',
                                            borderColor: hasPastDueActivities ? 'error.light' : 'primary.light',
                                            boxShadow: hasPastDueActivities ? '0 2px 8px rgba(239, 68, 68, 0.15)' : '0 2px 4px rgba(0,0,0,0.04)',
                                            '&:hover': {
                                              transform: 'translateY(-2px)',
                                              boxShadow: hasPastDueActivities ? '0 4px 12px rgba(239, 68, 68, 0.2)' : '0 4px 12px rgba(0,0,0,0.08)',
                                            }
                                          }}>
                                            {/* Always show warning icon for past due activities */}
                                            {hasPastDueActivities && (
                                              <Icons.WarningAmberOutlined 
                                                fontSize="small" 
                                                sx={{ 
                                                  color: 'error.main',
                                                  mb: zoomLevel === 'compact' ? 0 : 0.25,
                                                  fontSize: zoomLevel === 'compact' ? '0.85rem' : '0.95rem'
                                                }} 
                                              />
                                            )}
                                            {/* Only show number in non-compact view */}
                                            {zoomLevel !== 'compact' && (
                                              <mui.Typography 
                                                variant="caption" 
                                                fontWeight={600}
                                                sx={{
                                                  color: hasPastDueActivities ? 'error.main' : 'inherit'
                                                }}
                                              >
                                                {activitiesInWeek.length}
                                              </mui.Typography>
                                            )}
                                            {zoomLevel !== 'compact' && (
                                              <mui.Typography 
                                                variant="caption" 
                                                sx={{ 
                                                  fontSize: '0.65rem',
                                                  color: hasPastDueActivities ? 'error.main' : 'inherit'
                                                }}
                                              >
                                                {activitiesInWeek.length === 1 ? 'activity' : 'activities'}
                                              </mui.Typography>
                                            )}
                                          </mui.Box>
                                        )}
                                      </mui.TableCell>
                                    );
                                  })
                                ))}
                              </mui.TableRow>
                            );
                          })
                        ) : (
                          // Individual activity rows
                          Array.from(new Set(planItems.map(item => item.PHASE)))
                            .sort((a, b) => sortPhasesByOrder([a, b])[0] === a ? -1 : 1)
                            .map((phase, phaseIndex) => {
                            // Get all activities for this phase
                            const phaseActivities = planItems.filter(item => item.PHASE === phase);
                            
                            return phaseActivities.map((activity, activityIndex) => (
                              <mui.TableRow 
                                key={`${activity.id}-${activityIndex}`}
                                id={`activity-row-${activity.id}`}
                                sx={{ 
                                  backgroundColor: activityIndex % 2 === 0 ? 'rgba(255, 255, 255, 0.8)' : 'rgba(250, 251, 255, 0.5)',
                                  '&:hover': { 
                                    backgroundColor: 'rgba(240, 249, 255, 0.7)',
                                    '& .MuiTableCell-root': {
                                      borderBottomColor: 'rgba(25, 118, 210, 0.1)',
                                    },
                                    '& .activity-actions': {
                                      opacity: 1
                                    }
                                  },
                                  '&.highlight-new-activity': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                    transition: 'background-color 0.5s ease-in-out'
                                  }
                                }}
                              >
                                <mui.TableCell 
                                  className={`sticky-column ${activityIndex % 2 !== 0 ? 'sticky-column-alt' : ''}`}
                                  sx={{ 
                                    width: zoomLevel === 'compact' ? 220 : (zoomLevel === 'detailed' ? 500 : 400),
                                    minWidth: zoomLevel === 'compact' ? 220 : (zoomLevel === 'detailed' ? 500 : 400),
                                    borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                                    borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                                    fontWeight: activityIndex === 0 ? 600 : 400,
                                    position: 'sticky',
                                    left: 0,
                                    bgcolor: activityIndex % 2 === 0 ? 'white' : 'rgba(250, 251, 255, 0.9)',
                                    zIndex: 1,
                                    boxShadow: '2px 0 5px rgba(0,0,0,0.02)',
                                    py: zoomLevel === 'compact' ? 0.5 : zoomLevel === 'detailed' ? 2 : 1.5
                                  }}
                                >
                                  <mui.Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start'
                                  }}>
                                    <mui.Box>
                                      {activityIndex === 0 && (
                                        <mui.Box sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          mb: 1
                                        }}>
                                          <mui.Typography 
                                            variant={zoomLevel === 'compact' ? 'body2' : 'subtitle2'} 
                                            sx={{ 
                                              color: 'primary.main', 
                                              fontWeight: 600, 
                                              display: 'flex',
                                              alignItems: 'center'
                                            }}
                                          >
                                            <mui.Box 
                                              component="span" 
                                              sx={{ 
                                                width: zoomLevel === 'compact' ? 6 : 8, 
                                                height: zoomLevel === 'compact' ? 6 : 8, 
                                                borderRadius: '50%', 
                                                bgcolor: 'primary.main', 
                                                display: 'inline-block',
                                                mr: 1
                                              }}
                                            />
                                            {phase}
                                          </mui.Typography>
                                          
                                          {/* Quick Add Button for First Activity */}
                                          <mui.Tooltip title="Add new activity to this phase">
                                            <mui.IconButton
                                              size="small"
                                              onClick={(e) => handleQuickAddClick(e, phase)}
                                              sx={{ 
                                                color: 'primary.main',
                                                ml: 1
                                              }}
                                            >
                                              <AddCircleOutline fontSize="small" />
                                            </mui.IconButton>
                                          </mui.Tooltip>
                                        </mui.Box>
                                      )}
                                      <mui.Typography 
                                        variant={zoomLevel === 'compact' ? 'caption' : 'body2'}
                                        noWrap={zoomLevel === 'compact'}
                                        title={activity.ACTIVITY}
                                        sx={{ 
                                          maxWidth: zoomLevel === 'compact' ? 200 : (zoomLevel === 'detailed' ? 430 : 330),
                                          fontSize: zoomLevel === 'compact' ? '0.75rem' : 'inherit',
                                          ...(zoomLevel === 'compact' && {
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap'
                                          }),
                                          ...(zoomLevel === 'detailed' && {
                                            lineHeight: 1.5,
                                            display: '-webkit-box',
                                            overflow: 'hidden',
                                            WebkitBoxOrient: 'vertical',
                                            WebkitLineClamp: 3
                                          })
                                        }}
                                      >
                                        {activity.ACTIVITY}
                                      </mui.Typography>
                                      {(zoomLevel !== 'compact' && activity.owner_details) && (
                                        <mui.Typography 
                                          variant="caption" 
                                          sx={{ 
                                            display: 'block', 
                                            mt: 0.5, 
                                            color: 'text.secondary',
                                            fontSize: '0.7rem'
                                          }}
                                        >
                                          Owner: {activity.owner_details.first_name} {activity.owner_details.last_name}
                                        </mui.Typography>
                                      )}
                                    </mui.Box>
                                    
                                    {/* Inline Activity Actions */}
                                    <mui.Box 
                                      className="activity-actions"
                                      sx={{ 
                                        opacity: 0,
                                        transition: 'opacity 0.2s ease',
                                        display: 'flex',
                                        gap: 0.5
                                      }}
                                    >
                                      <mui.Tooltip title="Insert activity below">
                                        <mui.IconButton
                                          size="small"
                                          onClick={(e) => handleInsertActivityClick(e, phase, activity, activity.id)}
                                          sx={{ color: 'primary.main' }}
                                        >
                                          <AddBox fontSize="small" />
                                        </mui.IconButton>
                                      </mui.Tooltip>
                                      <mui.Tooltip title="Edit activity">
                                        <mui.IconButton
                                          size="small"
                                          onClick={() => handleEdit(activity)}
                                          sx={{ color: 'primary.main' }}
                                        >
                                          <Edit fontSize="small" />
                                        </mui.IconButton>
                                      </mui.Tooltip>
                                      <mui.Tooltip title="Clone activity">
                                        <mui.IconButton
                                          size="small"
                                          onClick={() => handleCloneActivity(activity)}
                                          sx={{ color: 'primary.main' }}
                                        >
                                          <ContentCopy fontSize="small" />
                                        </mui.IconButton>
                                      </mui.Tooltip>
                                      <mui.Tooltip title="Delete activity">
                                        <mui.IconButton
                                          size="small"
                                          onClick={() => confirmDelete(activity.id, activity.ACTIVITY)}
                                          sx={{ color: 'error.main' }}
                                        >
                                          <DeleteOutline fontSize="small" />
                                        </mui.IconButton>
                                      </mui.Tooltip>
                                    </mui.Box>
                                  </mui.Box>
                                </mui.TableCell>
                                
                                {/* Generate cells for each week of each month */}
                                {getTimelineMonths(timelineStartDate, extendedView).map((month, monthIndex) => (
                                  month.weeks.map((week, weekIndex) => {
                                    // Check if activity falls in this week
                                    const isActive = isActivityInWeek(activity, monthIndex, weekIndex, getTimelineMonths(timelineStartDate, extendedView));
                                    // Check if activity is past due
                                    const isPastDueActivity = isPastDue(activity);
                                    
                                    // Calculate which color to use
                                    const cellColor = isPastDueActivity 
                                      ? statusColors['Past Due'].barColor 
                                      : getChartStatusColor(activity.STATUS);
                                    
                                    // Create background gradient based on status
                                    const cellGradient = isPastDueActivity
                                      ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.9), rgba(239, 68, 68, 0.7))'
                                      : activity.STATUS === 'Completed' 
                                        ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.9), rgba(34, 197, 94, 0.7))' 
                                        : activity.STATUS === 'In Progress'
                                          ? 'linear-gradient(135deg, rgba(14, 165, 233, 0.9), rgba(14, 165, 233, 0.7))'
                                          : 'linear-gradient(135deg, rgba(148, 163, 184, 0.8), rgba(148, 163, 184, 0.6))';
                                    
                                    return (
                                      <mui.TableCell 
                                        key={`${activity.id}-${month.month}-${week}`}
                                        align="center"
                                        data-month-index={monthIndex}
                                        data-week-index={weekIndex}
                                        data-activity-id={activity.id}
                                        data-phase={phase}
                                        onMouseDown={(e) => {
                                          // Only trigger scheduling for unscheduled activities
                                          if (activity && !activity.START_DATE && !activity.END_DATE) {
                                            // Prevent default to avoid text selection during drag
                                            e.preventDefault();
                                            e.stopPropagation();
                                          handleMouseDown(phase, activity, monthIndex, weekIndex);
                                          }
                                          // For scheduled activities, the onClick handler in the activity box
                                          // will manage status changes
                                        }}
                                        // Ensure we have onMouseOver for drag selection
                                        onMouseOver={(e) => {
                                          if (isSelecting && activity && !activity.START_DATE && !activity.END_DATE) {
                                            // Get the cell's data attributes
                                            const cell = e.currentTarget;
                                            const cellMonthIndex = parseInt(cell.dataset.monthIndex);
                                            const cellWeekIndex = parseInt(cell.dataset.weekIndex);
                                            
                                            // Update the selection end if we're in selection mode
                                            if (isSelecting && selectionStart) {
                                              updateSelection(cellMonthIndex, cellWeekIndex);
                                            }
                                          }
                                        }}
                                        sx={{ 
                                          borderRight: weekIndex < 3 || monthIndex < (extendedView ? 5 : 2) ? '1px solid rgba(224, 224, 224, 0.5)' : 'none',
                                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                                          backgroundColor: isCellSelected(monthIndex, weekIndex, activity.id) ? 'rgba(59, 130, 246, 0.25)' :
                                                        (isActive ? 'transparent' : isPastDueActivity ? 'rgba(254, 226, 226, 0.3)' : 'transparent'),
                                          transition: 'all 0.2s ease',
                                          position: 'relative',
                                          height: zoomLevel === 'compact' ? 16 : zoomLevel === 'detailed' ? 70 : 40,
                                          padding: zoomLevel === 'compact' ? 0 : undefined,
                                          minWidth: zoomLevel === 'compact' ? 36 : (zoomLevel === 'detailed' ? 160 : 120),
                                          width: zoomLevel === 'compact' ? 36 : 'auto',
                                          maxWidth: zoomLevel === 'compact' ? 36 : (zoomLevel === 'detailed' ? 240 : 180),
                                          cursor: (!activity.START_DATE && !activity.END_DATE) ? 'crosshair' : 'pointer',
                                          userSelect: 'none', // Prevent text selection during drag
                                          ...(isCellSelected(monthIndex, weekIndex, activity.id) && {
                                            boxShadow: 'inset 0 0 0 2px rgba(59, 130, 246, 0.6)',
                                            zIndex: 1,
                                          }),
                                          '&:hover': {
                                            backgroundColor: isSelecting ? 'rgba(59, 130, 246, 0.1)' : (!activity.START_DATE && !activity.END_DATE) ? 'rgba(37, 99, 235, 0.08)' : 'rgba(25, 118, 210, 0.04)',
                                            // Show scheduling hint only for unscheduled activities
                                            '&::after': (!activity.START_DATE && !activity.END_DATE && !isActive && !isSelecting) ? {
                                              content: '"Drag to schedule"',
                                              position: 'absolute',
                                              top: '50%',
                                              left: '50%',
                                              transform: 'translate(-50%, -50%)',
                                              fontSize: '0.65rem',
                                              backgroundColor: 'rgba(0,0,0,0.7)',
                                              color: 'white',
                                              padding: '2px 6px',
                                              borderRadius: '4px',
                                              opacity: 0.9,
                                              zIndex: 5,
                                              pointerEvents: 'none',
                                              whiteSpace: 'nowrap'
                                            } : {}
                                          }
                                        }}
                                      >
                                        {isActive && (
                                          <mui.Tooltip 
                                            title={
                                              <React.Fragment>
                                                <mui.Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                  {activity.ACTIVITY}
                                                </mui.Typography>
                                                <mui.Box sx={{ 
                                                  display: 'flex', 
                                                  justifyContent: 'space-between', 
                                                  mt: 1,
                                                  gap: 2
                                                }}>
                                                  <mui.Box>
                                                    <mui.Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                                      Start Date
                                                    </mui.Typography>
                                                    <mui.Typography variant="body2">
                                                      {formatDate(activity.START_DATE)}
                                                    </mui.Typography>
                                                  </mui.Box>
                                                  <mui.Box>
                                                    <mui.Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                                                      End Date
                                                    </mui.Typography>
                                                    <mui.Typography variant="body2">
                                                      {formatDate(activity.END_DATE)}
                                                    </mui.Typography>
                                                  </mui.Box>
                                                </mui.Box>
                                                {isPastDueActivity && (
                                                  <mui.Box sx={{ 
                                                    bgcolor: 'error.lighter', 
                                                    color: 'error.main', 
                                                    px: 1, 
                                                    py: 0.5, 
                                                    borderRadius: 1,
                                                    mt: 1,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem'
                                                  }}>
                                                    <Icons.Warning fontSize="small" sx={{ mr: 0.5 }} />
                                                    Past Due
                                                  </mui.Box>
                                                )}
                                              </React.Fragment>
                                            }
                                            placement="top"
                                            arrow
                                          >
                                            <mui.Box
                                              sx={{
                                                position: 'absolute',
                                                top: '4px',
                                                left: '4px',
                                                right: '4px',
                                                bottom: '4px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexDirection: 'column',
                                                borderRadius: '4px',
                                                backgroundColor: cellColor,
                                                backgroundImage: cellGradient,
                                                color: isPastDueActivity || activity.STATUS === 'Completed' || activity.STATUS === 'In Progress' ? '#ffffff' : '#2d3748',
                                                fontWeight: 500,
                                                fontSize: '0.75rem',
                                                boxShadow: isPastDueActivity ? '0 2px 8px rgba(239, 68, 68, 0.2)' : '0 2px 8px rgba(0,0,0,0.08)',
                                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                                cursor: 'pointer',
                                                overflow: 'hidden',
                                                '&:hover': {
                                                  transform: 'translateY(-2px)',
                                                  boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                                  zIndex: 10
                                                }
                                              }}
                                              onClick={(e) => handleStatusPopoverOpen(e, activity)}
                                            >
                                              {/* Show status icons in all views */}
                                              <>
                                                {isPastDueActivity && (
                                                  <Icons.WarningAmberOutlined 
                                                    fontSize={zoomLevel === 'compact' ? 'small' : 'medium'} 
                                                    sx={{ mb: zoomLevel === 'compact' ? 0 : 0.5 }} 
                                                  />
                                                )}
                                                {!isPastDueActivity && activity.STATUS === 'Completed' && (
                                                  <Icons.CheckCircleOutlined 
                                                    fontSize={zoomLevel === 'compact' ? 'small' : 'medium'} 
                                                    sx={{ mb: zoomLevel === 'compact' ? 0 : 0.5 }} 
                                                  />
                                                )}
                                                {!isPastDueActivity && activity.STATUS === 'In Progress' && (
                                                  <Icons.RunCircleOutlined 
                                                    fontSize={zoomLevel === 'compact' ? 'small' : 'medium'} 
                                                    sx={{ mb: zoomLevel === 'compact' ? 0 : 0.5 }} 
                                                  />
                                                )}
                                                {!isPastDueActivity && activity.STATUS === 'Not Started' && (
                                                  <Icons.PendingOutlined 
                                                    fontSize={zoomLevel === 'compact' ? 'small' : 'medium'} 
                                                    sx={{ mb: zoomLevel === 'compact' ? 0 : 0.5 }} 
                                                  />
                                                )}
                                              </>
                                              
                                              {/* Add date indicator for non-compact views */}
                                              {zoomLevel !== 'compact' && (
                                                <mui.Typography 
                                                  variant="caption" 
                                                  sx={{ 
                                                    fontSize: '0.65rem',
                                                    fontWeight: 600,
                                                    lineHeight: 1,
                                                    mt: 0.5,
                                                    opacity: 0.9
                                                  }}
                                                >
                                                  {isFirstWeekOfActivity(activity, monthIndex, weekIndex, getTimelineMonths(timelineStartDate, extendedView)) && 
                                                   activity.START_DATE ? 
                                                    format(new Date(activity.START_DATE), 'MMM d') : 
                                                    isLastWeekOfActivity(activity, monthIndex, weekIndex, getTimelineMonths(timelineStartDate, extendedView)) && 
                                                    activity.END_DATE && 
                                                    !isFirstWeekOfActivity(activity, monthIndex, weekIndex, getTimelineMonths(timelineStartDate, extendedView)) ? 
                                                      format(new Date(activity.END_DATE), 'MMM d') : 
                                                      ''
                                                  }
                                                </mui.Typography>
                                              )}
                                            </mui.Box>
                                          </mui.Tooltip>
                                        )}
                                        
                                        {/* Show a subtle indicator for past due cells even when not active */}
                                        {!isActive && isPastDueActivity && zoomLevel !== 'compact' && (
                                          <mui.Box 
                                            sx={{
                                              position: 'absolute',
                                              top: '50%',
                                              left: '50%',
                                              transform: 'translate(-50%, -50%)',
                                              opacity: 0.5
                                            }}
                                          >
                                            <Icons.WarningAmberOutlined 
                                              fontSize="small" 
                                              sx={{ color: 'error.main' }} 
                                            />
                                          </mui.Box>
                                        )}
                                      </mui.TableCell>
                                    );
                                  })
                                ))}
                              </mui.TableRow>
                            ));
                          })
                        )}
                        
                        {/* Show message if no activities visible in current time range */}
                        {planItems.length > 0 && Array.from(new Set(planItems.map(item => item.PHASE))).length === 0 && (
                          <mui.TableRow>
                            <mui.TableCell colSpan={1 + (extendedView ? 24 : 12)} align="center" sx={{ 
                              py: 6, 
                              position: 'relative', 
                              left: 0,
                              zIndex: 1
                            }}>
                              <mui.Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Icons.DateRange sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                                <mui.Typography variant="subtitle1" color="text.secondary" gutterBottom fontWeight={500}>
                                  No activities in this time period
                                </mui.Typography>
                                <mui.Typography variant="body2" color="text.secondary">
                                  Try a different time range or add activities for this period
                                </mui.Typography>
                              </mui.Box>
                            </mui.TableCell>
                          </mui.TableRow>
                        )}
                      </mui.TableBody>
                    </mui.Table>
                  </mui.Box>
                  
                  <mui.Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid rgba(224, 224, 224, 0.3)'
                  }}>
                    <mui.Button 
                      startIcon={<Icons.Today />}
                      onClick={() => setTimelineStartDate(new Date())}
                      size="small"
                      sx={{
                        borderRadius: '6px',
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        border: '1px solid rgba(0,0,0,0.08)',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        '&:hover': {
                          backgroundColor: 'rgba(255,255,255,1)',
                        }
                      }}
                    >
                      Current Month
                    </mui.Button>
                    
                    <mui.Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      gap: 3,
                      flexWrap: 'wrap'
                    }}>
                      {Object.entries(statusColors).map(([status, { barColor, icon }]) => (
                        <mui.Box key={status} sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          padding: '4px 12px',
                          borderRadius: '16px',
                          backgroundColor: status === 'Completed' 
                            ? 'rgba(34, 197, 94, 0.1)' 
                            : status === 'In Progress' 
                              ? 'rgba(14, 165, 233, 0.1)' 
                              : status === 'Past Due'
                                ? 'rgba(239, 68, 68, 0.1)'
                                : 'rgba(148, 163, 184, 0.1)',
                          border: '1px solid',
                          borderColor: status === 'Completed' 
                            ? 'rgba(34, 197, 94, 0.2)' 
                            : status === 'In Progress' 
                              ? 'rgba(14, 165, 233, 0.2)' 
                              : status === 'Past Due'
                                ? 'rgba(239, 68, 68, 0.2)'
                                : 'rgba(148, 163, 184, 0.2)',
                        }}>
                          {status === 'Completed' && <Icons.CheckCircleOutlined fontSize="small" sx={{ color: 'rgb(34, 197, 94)' }} />}
                          {status === 'In Progress' && <Icons.RunCircleOutlined fontSize="small" sx={{ color: 'rgb(14, 165, 233)' }} />}
                          {status === 'Not Started' && <Icons.PendingOutlined fontSize="small" sx={{ color: 'rgb(148, 163, 184)' }} />}
                          {status === 'Past Due' && <Icons.WarningAmberOutlined fontSize="small" sx={{ color: 'rgb(239, 68, 68)' }} />}
                          <mui.Typography variant="caption" sx={{ 
                            color: status === 'Completed' 
                              ? 'rgb(34, 197, 94)' 
                              : status === 'In Progress' 
                                ? 'rgb(14, 165, 233)' 
                                : status === 'Past Due'
                                  ? 'rgb(239, 68, 68)'
                                  : 'rgb(71, 85, 105)',
                              fontWeight: 600
                            }}>
                            {status}
                          </mui.Typography>
                        </mui.Box>
                      ))}
                    </mui.Box>
                  </mui.Box>

                  <mui.Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    mt: 2,
                    pb: 1,
                    borderRadius: 1,
                    bgcolor: 'info.lighter',
                    color: 'info.dark',
                    p: 2,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    border: '1px solid',
                    borderColor: 'info.light'
                  }}>
                    <Icons.DragIndicator sx={{ mr: 1, fontSize: '1.25rem' }} />
                    <mui.Box>
                      <mui.Typography variant="subtitle2" fontWeight="600" gutterBottom={false}>
                        Pro Tip: Click and Drag to Set Activity Dates
                      </mui.Typography>
                      <mui.Typography variant="body2">
                        For unscheduled activities, click on a week cell and drag across multiple weeks to select a date range. 
                        Release to open the scheduling dialog with your selected date range.
                    </mui.Typography>
                    </mui.Box>
                  </mui.Box>
                </mui.Paper>
              )}

              {/* Plan Items List */}
              <mui.Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 2,
                mt: 4
              }}>
                <mui.Typography variant="h6" fontWeight="600">
                  Planning Activities
                </mui.Typography>
                
                <mui.ToggleButtonGroup
                  value={activitiesView}
                  exclusive
                  onChange={(e, newView) => {
                    if (newView !== null) setActivitiesView(newView);
                  }}
                  size="small"
                  sx={{ 
                    '& .MuiToggleButton-root': {
                      border: '1px solid rgba(0,0,0,0.08)',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '0.8rem',
                      fontWeight: 500
                    },
                    '& .MuiToggleButton-root.Mui-selected': {
                      backgroundColor: 'primary.lighter',
                      color: 'primary.main',
                      borderColor: 'primary.light'
                    }
                  }}
                >
                  <mui.ToggleButton value="card">
                    <Icons.ViewModule fontSize="small" sx={{ mr: 0.5, fontSize: '1rem' }} />
                    Card View
                  </mui.ToggleButton>
                  <mui.ToggleButton value="list">
                    <Icons.ViewList fontSize="small" sx={{ mr: 0.5, fontSize: '1rem' }} />
                    List View
                  </mui.ToggleButton>
                </mui.ToggleButtonGroup>
              </mui.Box>
              
              {planItems.length === 0 ? (
                <mui.Paper sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'rgba(148, 163, 184, 0.2)',
                  bgcolor: 'background.paper'
                }}>
                  <Icons.EventNote sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <mui.Typography variant="h6" color="text.secondary" gutterBottom>
                    No Plan Items Yet
                  </mui.Typography>
                  <mui.Typography variant="body2" color="text.secondary" paragraph>
                    Start by adding phases and activities to create your audit plan.
                  </mui.Typography>
                  <mui.Button
                    variant="outlined"
                    color="primary"
                    startIcon={<Icons.Add />}
                    onClick={() => setFormOpen(true)}
                  >
                    Add Plan Item
                  </mui.Button>
                </mui.Paper>
              ) : (
                // Group activities by phase
                <>
                  {activitiesView === 'card' ? (
                    // Card View
                    Array.from(new Set(planItems.map(item => item.PHASE)))
                      .sort((a, b) => sortPhasesByOrder([a, b])[0] === a ? -1 : 1)
                      .map((phase, phaseIndex) => {
                      // Get all activities for this phase
                      const phaseActivities = planItems.filter(item => item.PHASE === phase);
                      
                      return (
                        <mui.Box key={`phase-group-${phaseIndex}`} sx={{ mb: 4 }}>
                          <mui.Box 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              mb: 2,
                              mt: phaseIndex > 0 ? 3 : 0
                            }}
                          >
                            <mui.Box 
                              sx={{ 
                                width: 4, 
                                height: 20, 
                                bgcolor: 'primary.main', 
                                borderRadius: 1,
                                mr: 1.5
                              }} 
                            />
                            <mui.Typography 
                              variant="h6" 
                              fontWeight="600"
                              color="primary.main"
                              sx={{ 
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              {phase}
                              <mui.Chip 
                                label={`${phaseActivities.length} ${phaseActivities.length === 1 ? 'Activity' : 'Activities'}`}
                                size="small"
                                sx={{ 
                                  ml: 2, 
                                  bgcolor: 'primary.lighter',
                                  color: 'primary.dark',
                                  height: 24,
                                  fontSize: '0.75rem'
                                }}
                              />
                            </mui.Typography>
                          </mui.Box>
                          
                          <mui.Grid container spacing={2}>
                            {phaseActivities.map((item) => (
                              <mui.Grid item xs={12} md={6} lg={4} key={item.id}>
                                <mui.Paper sx={{ 
                                  p: 3, 
                                  height: '100%',
                                  borderRadius: 2,
                                  border: '1px solid',
                                  borderColor: isPastDue(item) ? 'rgba(239, 68, 68, 0.3)' : 'rgba(148, 163, 184, 0.1)',
                                  transition: 'all 0.2s ease-in-out',
                                  position: 'relative',
                                  boxShadow: isPastDue(item) ? '0 4px 12px rgba(239, 68, 68, 0.08)' : 'none',
                                  '&:hover': {
                                    boxShadow: isPastDue(item) ? '0 8px 24px rgba(239, 68, 68, 0.12)' : '0 4px 20px rgba(0,0,0,0.05)',
                                    transform: 'translateY(-2px)'
                                  }
                                }}>
                                  {isPastDue(item) && (
                                    <mui.Box 
                                      sx={{ 
                                        position: 'absolute',
                                        top: -10,
                                        right: -10,
                                        bgcolor: 'error.main',
                                        color: 'white',
                                        borderRadius: '50%',
                                        width: 30,
                                        height: 30,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        zIndex: 1
                                      }}
                                    >
                                      <Icons.Warning fontSize="small" />
                                    </mui.Box>
                                  )}
                                  <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <mui.Chip 
                                      label={isPastDue(item) ? 'Past Due' : item.STATUS}
                                      size="small"
                                      sx={{ 
                                        bgcolor: isPastDue(item) ? statusColors['Past Due'].bg : statusColors[item.STATUS]?.bg || '#f8fafc', 
                                        color: isPastDue(item) ? statusColors['Past Due'].color : statusColors[item.STATUS]?.color || '#475569',
                                        fontWeight: 500,
                                        borderRadius: '6px'
                                      }}
                                    />
                                    <mui.Box>
                                      <mui.IconButton 
                                        size="small" 
                                        onClick={() => handleEdit(item)}
                                        sx={{ color: 'text.secondary' }}
                                      >
                                        <Icons.Edit fontSize="small" />
                                      </mui.IconButton>
                                      <mui.IconButton 
                                        size="small" 
                                        onClick={() => handleDelete(item.id)}
                                        sx={{ color: 'text.secondary' }}
                                      >
                                        <Icons.Delete fontSize="small" />
                                      </mui.IconButton>
                                    </mui.Box>
                                  </mui.Box>
                                  
                                  <mui.Typography variant="body1" fontWeight="500" mb={1} sx={{ lineHeight: 1.4 }}>
                                    {item.ACTIVITY}
                                  </mui.Typography>
                                  
                                  <mui.Typography variant="subtitle2" color="text.secondary" mt={2}>
                                    Assigned To
                                  </mui.Typography>
                                  <mui.Typography variant="body2" mb={1}>
                                    {item.owner_details 
                                      ? `${item.owner_details.first_name} ${item.owner_details.last_name}`
                                      : 'Unassigned'}
                                  </mui.Typography>
                                  
                                  <mui.Box sx={{ display: 'flex', mt: 2 }}>
                                    <mui.Box sx={{ width: '50%' }}>
                                      <mui.Typography variant="subtitle2" color="text.secondary">
                                        Start Date
                                      </mui.Typography>
                                      <mui.Typography variant="body2">
                                        {formatDate(item.START_DATE)}
                                      </mui.Typography>
                                    </mui.Box>
                                    <mui.Box sx={{ width: '50%' }}>
                                      <mui.Typography variant="subtitle2" color="text.secondary">
                                        End Date
                                      </mui.Typography>
                                      <mui.Typography variant="body2">
                                        {formatDate(item.END_DATE)}
                                      </mui.Typography>
                                    </mui.Box>
                                  </mui.Box>
                                  
                                  {/* Status update buttons */}
                                  <mui.Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 1 }}>
                                    {isPastDue(item) && !['Completed'].includes(item.STATUS) && (
                                      <mui.Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        sx={{ 
                                          borderRadius: '6px',
                                          borderColor: 'error.main',
                                          py: 0.5,
                                          fontWeight: 600,
                                          minWidth: '120px',
                                          boxShadow: '0 2px 5px rgba(239, 68, 68, 0.15)'
                                        }}
                                        onClick={() => handleStatusChange(item.id, 'Completed')}
                                      >
                                        Mark Complete
                                      </mui.Button>
                                    )}
                                    <mui.Button
                                      size="small"
                                      variant="outlined"
                                      color="primary"
                                      onClick={() => handleStatusChange(item.id, 'Not Started')}
                                      disabled={item.STATUS === 'Not Started'}
                                      sx={{ 
                                        borderRadius: '6px', 
                                        borderColor: item.STATUS === 'Not Started' ? 'primary.main' : 'rgba(148, 163, 184, 0.3)',
                                        opacity: item.STATUS === 'Not Started' ? 1 : 0.7
                                      }}
                                    >
                                      Not Started
                                    </mui.Button>
                                    <mui.Button
                                      size="small"
                                      variant="outlined"
                                      color="info"
                                      onClick={() => handleStatusChange(item.id, 'In Progress')}
                                      disabled={item.STATUS === 'In Progress'}
                                      sx={{ 
                                        borderRadius: '6px',
                                        borderColor: item.STATUS === 'In Progress' ? 'info.main' : 'rgba(148, 163, 184, 0.3)',
                                        opacity: item.STATUS === 'In Progress' ? 1 : 0.7
                                      }}
                                    >
                                      In Progress
                                    </mui.Button>
                                    <mui.Button
                                      size="small"
                                      variant="outlined"
                                      color="success"
                                      onClick={() => handleStatusChange(item.id, 'Completed')}
                                      disabled={item.STATUS === 'Completed'}
                                      sx={{ 
                                        borderRadius: '6px',
                                        borderColor: item.STATUS === 'Completed' ? 'success.main' : 'rgba(148, 163, 184, 0.3)',
                                        opacity: item.STATUS === 'Completed' ? 1 : 0.7
                                      }}
                                    >
                                      Completed
                                    </mui.Button>
                                  </mui.Box>
                                </mui.Paper>
                              </mui.Grid>
                            ))}
                          </mui.Grid>
                        </mui.Box>
                      );
                    })
                  ) : (
                    // List View
                    <mui.Paper 
                      sx={{ 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'rgba(148, 163, 184, 0.1)',
                        boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
                        overflow: 'hidden'
                      }}
                    >
                      {Array.from(new Set(planItems.map(item => item.PHASE)))
                        .sort((a, b) => sortPhasesByOrder([a, b])[0] === a ? -1 : 1)
                        .map((phase, phaseIndex) => {
                        // Get all activities for this phase
                        const phaseActivities = planItems.filter(item => item.PHASE === phase);
                        
                        return (
                          <mui.Box key={`phase-list-${phaseIndex}`}>
                            {/* Phase Header */}
                            <mui.Box 
                              sx={{ 
                                bgcolor: 'primary.lighter',
                                px: 3,
                                py: 1.5,
                                borderBottom: '1px solid',
                                borderTop: phaseIndex > 0 ? '1px solid' : 'none',
                                borderColor: 'rgba(224, 224, 224, 0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}
                            >
                              <mui.Typography 
                                variant="subtitle1" 
                                fontWeight="600"
                                color="primary.dark"
                                sx={{ 
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                              >
                                <Icons.LabelImportant sx={{ mr: 1, fontSize: '1.2rem', color: 'primary.main' }} />
                                {phase}
                              </mui.Typography>
                              <mui.Chip 
                                label={`${phaseActivities.length} ${phaseActivities.length === 1 ? 'Activity' : 'Activities'}`}
                                size="small"
                                sx={{ 
                                  bgcolor: 'rgba(255,255,255,0.6)',
                                  color: 'primary.dark',
                                  height: 24,
                                  fontSize: '0.75rem'
                                }}
                              />
                            </mui.Box>
                            
                            {/* Activities List */}
                            <mui.List disablePadding>
                              {phaseActivities.map((item, activityIndex) => (
                                <mui.ListItem 
                                  key={item.id}
                                  divider={activityIndex < phaseActivities.length - 1}
                                  alignItems="flex-start"
                                  sx={{ 
                                    px: 3, 
                                    py: 2,
                                    transition: 'background-color 0.2s',
                                    backgroundColor: isPastDue(item) ? 'rgba(254, 242, 242, 0.4)' : 'inherit',
                                    borderLeft: isPastDue(item) ? '4px solid #ef4444' : 'none',
                                    '&:hover': {
                                      bgcolor: isPastDue(item) ? 'rgba(254, 242, 242, 0.6)' : 'rgba(0,0,0,0.02)'
                                    }
                                  }}
                                >
                                  <mui.ListItemText
                                    primary={
                                      <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <mui.Typography variant="body1" fontWeight="500" sx={{ 
                                          display: 'flex',
                                          alignItems: 'center'
                                        }}>
                                          {isPastDue(item) && (
                                            <Icons.Warning 
                                              fontSize="small" 
                                              color="error" 
                                              sx={{ mr: 1 }} 
                                            />
                                          )}
                                          {item.ACTIVITY}
                                        </mui.Typography>
                                        <mui.Box>
                                          <mui.IconButton 
                                            size="small" 
                                            onClick={() => handleEdit(item)}
                                            sx={{ color: 'text.secondary', ml: 1 }}
                                          >
                                            <Icons.Edit fontSize="small" />
                                          </mui.IconButton>
                                          <mui.IconButton 
                                            size="small" 
                                            onClick={() => handleDelete(item.id)}
                                            sx={{ color: 'text.secondary' }}
                                          >
                                            <Icons.Delete fontSize="small" />
                                          </mui.IconButton>
                                        </mui.Box>
                                      </mui.Box>
                                    }
                                    secondary={
                                      <mui.Grid container spacing={2} sx={{ mt: 0.5 }}>
                                        <mui.Grid item xs={12} sm={4}>
                                          <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Icons.Person sx={{ fontSize: '1rem', mr: 1, color: 'text.secondary' }} />
                                            <mui.Typography variant="body2" color="text.secondary">
                                              {item.owner_details 
                                                ? `${item.owner_details.first_name} ${item.owner_details.last_name}`
                                                : 'Unassigned'}
                                            </mui.Typography>
                                          </mui.Box>
                                        </mui.Grid>
                                        <mui.Grid item xs={12} sm={3}>
                                          <mui.Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Icons.Event sx={{ 
                                              fontSize: '1rem', 
                                              mr: 1, 
                                              color: isPastDue(item) ? 'error.main' : 'text.secondary' 
                                            }} />
                                            <mui.Typography 
                                              variant="body2" 
                                              sx={{ 
                                                color: isPastDue(item) ? 'error.main' : 'text.secondary',
                                                fontWeight: isPastDue(item) ? 600 : 400
                                              }}
                                            >
                                              {formatDate(item.END_DATE)}
                                              {isPastDue(item) && (
                                                <mui.Typography 
                                                  component="span" 
                                                  sx={{ 
                                                    ml: 1, 
                                                    color: 'error.main', 
                                                    fontSize: '0.7rem',
                                                    fontWeight: 700
                                                  }}
                                                >
                                                  (OVERDUE)
                                                </mui.Typography>
                                              )}
                                            </mui.Typography>
                                          </mui.Box>
                                        </mui.Grid>
                                        <mui.Grid item xs={12} sm={2}>
                                          <mui.Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <mui.Chip 
                                              label={isPastDue(item) ? 'Past Due' : item.STATUS}
                                              size="small"
                                              icon={isPastDue(item) ? statusColors['Past Due'].icon : statusColors[item.STATUS]?.icon}
                                              sx={{ 
                                                bgcolor: isPastDue(item) ? statusColors['Past Due'].bg : statusColors[item.STATUS]?.bg || '#f8fafc', 
                                                color: isPastDue(item) ? statusColors['Past Due'].color : statusColors[item.STATUS]?.color || '#475569',
                                                fontWeight: 500,
                                                borderRadius: '6px',
                                                '& .MuiChip-icon': {
                                                  color: isPastDue(item) ? statusColors['Past Due'].color : statusColors[item.STATUS]?.color || '#475569',
                                                }
                                              }}
                                            />
                                          </mui.Box>
                                        </mui.Grid>
                                        <mui.Grid item xs={12}>
                                          <mui.Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                                            {isPastDue(item) && !['Completed'].includes(item.STATUS) && (
                                              <mui.Button
                                                size="small"
                                                variant="outlined"
                                                color="error"
                                                sx={{ 
                                                  borderRadius: '6px',
                                                  borderColor: 'error.main',
                                                  py: 0.5,
                                                  fontWeight: 600,
                                                  minWidth: '120px',
                                                  boxShadow: '0 2px 5px rgba(239, 68, 68, 0.15)'
                                                }}
                                                onClick={() => handleStatusChange(item.id, 'Completed')}
                                              >
                                                Mark Complete
                                              </mui.Button>
                                            )}
                                            <mui.Button
                                              size="small"
                                              variant="outlined"
                                              color="primary"
                                              onClick={() => handleStatusChange(item.id, 'Not Started')}
                                              disabled={item.STATUS === 'Not Started'}
                                              sx={{ 
                                                borderRadius: '6px', 
                                                py: 0.5,
                                                minWidth: '100px',
                                                borderColor: item.STATUS === 'Not Started' ? 'primary.main' : 'rgba(148, 163, 184, 0.3)',
                                                opacity: item.STATUS === 'Not Started' ? 1 : 0.7
                                              }}
                                            >
                                              Not Started
                                            </mui.Button>
                                            <mui.Button
                                              size="small"
                                              variant="outlined"
                                              color="info"
                                              onClick={() => handleStatusChange(item.id, 'In Progress')}
                                              disabled={item.STATUS === 'In Progress'}
                                              sx={{ 
                                                borderRadius: '6px',
                                                py: 0.5,
                                                minWidth: '100px',
                                                borderColor: item.STATUS === 'In Progress' ? 'info.main' : 'rgba(148, 163, 184, 0.3)',
                                                opacity: item.STATUS === 'In Progress' ? 1 : 0.7
                                              }}
                                            >
                                              In Progress
                                            </mui.Button>
                                            <mui.Button
                                              size="small"
                                              variant="outlined"
                                              color="success"
                                              onClick={() => handleStatusChange(item.id, 'Completed')}
                                              disabled={item.STATUS === 'Completed'}
                                              sx={{ 
                                                borderRadius: '6px',
                                                py: 0.5,
                                                minWidth: '100px',
                                                borderColor: item.STATUS === 'Completed' ? 'success.main' : 'rgba(148, 163, 184, 0.3)',
                                                opacity: item.STATUS === 'Completed' ? 1 : 0.7
                                              }}
                                            >
                                              Completed
                                            </mui.Button>
                                          </mui.Box>
                                        </mui.Grid>
                                      </mui.Grid>
                                    }
                                  />
                                </mui.ListItem>
                              ))}
                            </mui.List>
                          </mui.Box>
                        );
                      })}
                    </mui.Paper>
                  )}
                </>
              )}
            </>
          )}
        </mui.Box>
        
        {/* Add/Edit Form Dialog */}
        <mui.Dialog 
          open={formOpen} 
          onClose={resetForm}
          maxWidth="md"
          fullWidth
        >
          <mui.DialogTitle sx={{ fontWeight: 600 }}>
            {editItemId ? 'Edit Plan Item' : 'Add New Plan Item'}
          </mui.DialogTitle>
          <mui.DialogContent>
            <mui.Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <mui.Grid container spacing={3}>
                <mui.Grid item xs={12} sm={6}>
                  <mui.TextField
                    label="Phase"
                    value={phase}
                    onChange={(e) => setPhase(e.target.value)}
                    fullWidth
                    required
                    placeholder="e.g., Planning, Fieldwork, Reporting"
                    variant="outlined"
                  />
                </mui.Grid>
                <mui.Grid item xs={12} sm={6}>
                  <mui.FormControl fullWidth required>
                    <mui.InputLabel id="status-label">Status</mui.InputLabel>
                    <mui.Select
                      labelId="status-label"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      label="Status"
                    >
                      <mui.MenuItem value="Not Started">Not Started</mui.MenuItem>
                      <mui.MenuItem value="In Progress">In Progress</mui.MenuItem>
                      <mui.MenuItem value="Completed">Completed</mui.MenuItem>
                    </mui.Select>
                  </mui.FormControl>
                </mui.Grid>
                <mui.Grid item xs={12}>
                  <mui.TextField
                    label="Activity Description"
                    value={activity}
                    onChange={(e) => setActivity(e.target.value)}
                    fullWidth
                    required
                    multiline
                    rows={3}
                    placeholder="Describe what needs to be done in this activity"
                    variant="outlined"
                  />
                </mui.Grid>
                <mui.Grid item xs={12}>
                  <mui.FormControl fullWidth>
                    <mui.InputLabel id="owner-label">Action Owner</mui.InputLabel>
                    <mui.Select
                      labelId="owner-label"
                      value={owner}
                      onChange={(e) => setOwner(e.target.value)}
                      label="Action Owner"
                    >
                      <mui.MenuItem value="">
                        <em>Not Assigned</em>
                      </mui.MenuItem>
                      {console.log("Rendering internal audit users:", internalAuditUsers)}
                      {internalAuditUsers && internalAuditUsers.length > 0 ? (
                        internalAuditUsers.map((user) => (
                          <mui.MenuItem key={user.id} value={user.id}>
                            {user.first_name} {user.last_name} {user.email ? `(${user.email})` : ''}
                          </mui.MenuItem>
                        ))
                      ) : (
                        <mui.MenuItem disabled>
                          <em>No internal audit users available</em>
                        </mui.MenuItem>
                      )}
                    </mui.Select>
                  </mui.FormControl>
                </mui.Grid>
                <mui.Grid item xs={12} sm={6}>
                  <DatePicker
                    label="Start Date"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    renderInput={(params) => 
                      <mui.TextField {...params} fullWidth required />
                    }
                    inputFormat="MMM d, yyyy"
                  />
                </mui.Grid>
                <mui.Grid item xs={12} sm={6}>
                  <DatePicker
                    label="End Date"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    renderInput={(params) => 
                      <mui.TextField {...params} fullWidth required />
                    }
                    inputFormat="MMM d, yyyy"
                    minDate={startDate || undefined}
                  />
                </mui.Grid>
              </mui.Grid>
            </mui.Box>
          </mui.DialogContent>
          <mui.DialogActions sx={{ px: 3, pb: 3 }}>
            <mui.Button 
              onClick={resetForm}
              variant="outlined"
              sx={{ borderRadius: '8px' }}
            >
              Cancel
            </mui.Button>
            <mui.Button 
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              sx={{ borderRadius: '8px' }}
            >
              {editItemId ? 'Update' : 'Add'} Plan Item
            </mui.Button>
          </mui.DialogActions>
        </mui.Dialog>

        {/* Delete Confirmation Dialog */}
        <mui.Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <mui.DialogTitle sx={{ 
            fontWeight: 600,
            color: 'error.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Icons.Warning /> Delete Activity
          </mui.DialogTitle>
          <mui.DialogContent>
            <mui.Typography variant="body1" paragraph>
              Are you sure you want to delete this activity? This action cannot be undone.
            </mui.Typography>
            {activityToDelete && (
              <mui.Paper sx={{ p: 2, bgcolor: 'error.lighter', color: 'error.dark', borderRadius: 1, mb: 2 }}>
                <mui.Typography variant="subtitle2" fontWeight="600">
                  {activityToDelete.name}
                </mui.Typography>
              </mui.Paper>
            )}
            <mui.Typography variant="body2" color="text.secondary">
              All data associated with this activity will be permanently removed.
            </mui.Typography>
          </mui.DialogContent>
          <mui.DialogActions sx={{ px: 3, pb: 3 }}>
            <mui.Button 
              onClick={() => setDeleteDialogOpen(false)}
              variant="outlined"
              sx={{ borderRadius: '8px' }}
            >
              Cancel
            </mui.Button>
            <mui.Button 
              onClick={executeDelete}
              variant="contained"
              color="error"
              sx={{ borderRadius: '8px' }}
              startIcon={<Icons.Delete />}
            >
              Delete Activity
            </mui.Button>
          </mui.DialogActions>
        </mui.Dialog>

        {/* Quick Add Dialog/Popover - replace with enhanced version */}
        <mui.Popover
          open={quickAddOpen}
          anchorEl={anchorEl}
          onClose={handleQuickAddClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              p: 2,
              width: 320,
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
            }
          }}
        >
          <mui.Typography variant="subtitle2" fontWeight="600" mb={2}>
            {insertAfter ? 'Insert Activity After' : `Add Activity to ${quickAddPhase}`}
            {insertAfter && (
              <mui.Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                {insertAfter.ACTIVITY}
              </mui.Typography>
            )}
          </mui.Typography>
          
          <mui.Box component="form" onSubmit={(e) => {
            e.preventDefault();
            const activityName = e.target.elements.activityName.value;
            const finalPhase = insertAfter ? insertPhase : quickAddPhase;
            
            // If using new phase, get the name
            if (finalPhase === '__new__') {
              const newPhaseName = e.target.elements.newPhaseName.value;
              if (!newPhaseName) {
                enqueueSnackbar('Please enter a name for the new phase', { variant: 'warning' });
                return;
              }
            }
            
            // Pass the after activity ID
            handleQuickAddWithPosition(
              activityName, 
              finalPhase, 
              insertPosition,
              insertAfter ? insertAfter.id : null
            );
          }}>
            <mui.TextField
              id="activityName"
              name="activityName"
              label="Activity Name"
              fullWidth
              autoFocus
              required
              variant="outlined"
              placeholder="Enter activity name"
              size="small"
              sx={{ mb: 2 }}
            />
            
            {/* Phase selection if adding without a specific position */}
            {!insertAfter && (
              <mui.FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <mui.InputLabel id="phase-select-label">Phase</mui.InputLabel>
                <mui.Select
                  labelId="phase-select-label"
                  value={quickAddPhase}
                  label="Phase"
                  required
                  onChange={(e) => setQuickAddPhase(e.target.value)}
                >
                  {Array.from(new Set(planItems.map(item => item.PHASE)))
                    .sort((a, b) => sortPhasesByOrder([a, b])[0] === a ? -1 : 1)
                    .map((phase) => (
                      <mui.MenuItem key={phase} value={phase}>
                        {phase}
                      </mui.MenuItem>
                    ))}
                  {/* Allow adding a new phase if needed */}
                  <mui.MenuItem value="__new__">
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
                      <Add fontSize="small" sx={{ mr: 1 }} />
                      Add New Phase
                    </mui.Box>
                  </mui.MenuItem>
                </mui.Select>
              </mui.FormControl>
            )}
            
            {/* Show text field for new phase name if "__new__" is selected */}
            {quickAddPhase === '__new__' && (
              <mui.TextField
                name="newPhaseName"
                label="New Phase Name"
                fullWidth
                required
                variant="outlined"
                placeholder="Enter new phase name"
                size="small"
                sx={{ mb: 2 }}
              />
            )}
            
            <mui.Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <mui.Button
                onClick={handleQuickAddClose}
                size="small"
                sx={{ 
                  borderRadius: '6px'
                }}
              >
                Cancel
              </mui.Button>
              <mui.Button
                type="submit"
                variant="contained"
                color="primary"
                size="small"
                sx={{ 
                  borderRadius: '6px'
                }}
                startIcon={insertAfter ? <Icons.PlaylistAdd /> : <Icons.Add />}
              >
                {insertAfter ? 'Insert Activity' : 'Add Activity'}
              </mui.Button>
            </mui.Box>
          </mui.Box>
        </mui.Popover>

        {/* Delete Plan Confirmation Dialog */}
        <mui.Dialog
          open={deletePlanDialogOpen}
          onClose={() => setDeletePlanDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <mui.DialogTitle sx={{ 
            fontWeight: 600,
            color: 'error.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Icons.Warning /> Delete Planning Document
          </mui.DialogTitle>
          <mui.DialogContent>
            <mui.Typography variant="body1" paragraph>
              Are you sure you want to delete this entire planning document? This action cannot be undone.
            </mui.Typography>
            <mui.Paper sx={{ p: 2, bgcolor: 'error.lighter', color: 'error.dark', borderRadius: 1, mb: 2 }}>
              <mui.Typography variant="subtitle1" fontWeight="600">
                {documentInfo.name} ({document_type})
              </mui.Typography>
              <mui.Typography variant="body2" sx={{ mt: 1 }}>
                {planItems.length} activities will be permanently deleted
                      </mui.Typography>
            </mui.Paper>
            <mui.Box sx={{ mt: 2, display: 'flex', alignItems: 'center', borderRadius: 1, bgcolor: 'warning.lighter', p: 1 }}>
              <Icons.Info sx={{ mr: 1, color: 'warning.main' }} />
              <mui.Typography variant="caption" color="warning.dark">
                This will only delete the {document_type} plan. Other document types for this project will not be affected.
              </mui.Typography>
            </mui.Box>
          </mui.DialogContent>
          <mui.DialogActions sx={{ px: 3, pb: 3 }}>
            <mui.Button 
              onClick={() => setDeletePlanDialogOpen(false)}
              variant="outlined"
              sx={{ borderRadius: '8px' }}
            >
              Cancel
            </mui.Button>
            <mui.Button 
              onClick={handleDeleteEntirePlan}
              variant="contained"
              color="error"
              sx={{ borderRadius: '8px' }}
              startIcon={<Icons.Delete />}
            >
              Delete Plan
            </mui.Button>
          </mui.DialogActions>
        </mui.Dialog>

        {/* Timeline Controls Toolbar */}
        <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <mui.Box>
            <mui.Button
              variant="outlined"
              size="small"
              startIcon={<Icons.Today />}
              onClick={() => setDatePickerOpen(true)}
              sx={{ mr: 1, borderRadius: '8px' }}
            >
              Jump to Date
            </mui.Button>
            <mui.Button
              variant="outlined"
              size="small"
              startIcon={<Icons.CalendarToday />}
              onClick={() => setTimelineStartDate(new Date())}
              sx={{ borderRadius: '8px' }}
            >
              Today
            </mui.Button>
          </mui.Box>
          
          {/* Replace the verbose zoom controls with a minimalist view menu button */}
          <mui.Box>
            {/* View settings menu button */}
            <mui.Tooltip title="View Settings">
              <mui.IconButton
                aria-controls="view-menu"
                aria-haspopup="true"
                onClick={(event) => setViewMenuAnchor(event.currentTarget)}
                color="primary"
                sx={{ ml: 1 }}
              >
                <Icons.ViewCompact />
              </mui.IconButton>
            </mui.Tooltip>
            
            {/* View settings menu */}
            <mui.Menu
              id="view-menu"
              anchorEl={viewMenuAnchor}
              keepMounted
              open={Boolean(viewMenuAnchor)}
              onClose={() => setViewMenuAnchor(null)}
            >
              <mui.MenuItem disabled>
                <mui.Typography variant="subtitle2" color="text.secondary">
                  Zoom Level
                </mui.Typography>
              </mui.MenuItem>
              <mui.Divider />
              <mui.MenuItem 
                onClick={() => {
                  setZoomLevel('compact');
                  setViewMenuAnchor(null);
                }}
                selected={zoomLevel === 'compact'}
              >
                <Icons.ZoomOut sx={{ mr: 1 }} fontSize="small" />
                Compact View
              </mui.MenuItem>
              <mui.MenuItem 
                onClick={() => {
                  setZoomLevel('normal');
                  setViewMenuAnchor(null);
                }}
                selected={zoomLevel === 'normal'}
              >
                <Icons.ZoomIn sx={{ mr: 1 }} fontSize="small" />
                Normal View
              </mui.MenuItem>
              <mui.MenuItem 
                onClick={() => {
                  setZoomLevel('detailed');
                  setViewMenuAnchor(null);
                }}
                selected={zoomLevel === 'detailed'}
              >
                <Icons.ZoomInMap sx={{ mr: 1 }} fontSize="small" />
                Detailed View
              </mui.MenuItem>
              <mui.Divider />
              <mui.MenuItem disabled>
                <mui.Typography variant="subtitle2" color="text.secondary">
                  Activities Display
                </mui.Typography>
              </mui.MenuItem>
              <mui.Divider />
              <mui.MenuItem 
                onClick={() => {
                  setActivitiesView('card');
                  setViewMenuAnchor(null);
                }}
                selected={activitiesView === 'card'}
              >
                <Icons.ViewModule sx={{ mr: 1 }} fontSize="small" />
                Card View
              </mui.MenuItem>
              <mui.MenuItem 
                onClick={() => {
                  setActivitiesView('list');
                  setViewMenuAnchor(null);
                }}
                selected={activitiesView === 'list'}
              >
                <Icons.ViewList sx={{ mr: 1 }} fontSize="small" />
                List View
              </mui.MenuItem>
            </mui.Menu>
          </mui.Box>
        </mui.Box>

        {/* Status Update Popover */}
        <mui.Popover
          open={Boolean(statusPopoverAnchorEl)}
          anchorEl={statusPopoverAnchorEl}
          onClose={handleStatusPopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              overflow: 'visible',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-6px',
                left: '50%',
                transform: 'translateX(-50%) rotate(45deg)',
                width: '12px',
                height: '12px',
                backgroundColor: 'background.paper',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                borderLeft: '1px solid rgba(0,0,0,0.08)',
                zIndex: 0
              }
            }
          }}
        >
          <mui.Box sx={{ p: 1, width: 220 }}>
            <mui.Typography variant="subtitle2" fontWeight="600" sx={{ px: 1, py: 0.5 }}>
              Update Status
              {selectedStatusActivity && (
                <mui.Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ mt: 0.5 }}>
                  {selectedStatusActivity.ACTIVITY}
                </mui.Typography>
              )}
            </mui.Typography>
            
            <mui.Divider sx={{ my: 1 }} />
            
            <mui.List disablePadding>
              <mui.ListItem disablePadding>
                <mui.ListItemButton 
                  onClick={() => handleStatusChangeFromTimeline('Not Started')}
                  sx={{ 
                    borderRadius: '6px',
                    py: 0.75,
                    '&:hover': {
                      backgroundColor: 'rgba(148, 163, 184, 0.1)'
                    }
                  }}
                >
                  <mui.ListItemIcon sx={{ minWidth: 36 }}>
                    <mui.Box 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%', 
                        bgcolor: statusColors['Not Started'].bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Icons.PendingOutlined fontSize="small" sx={{ color: statusColors['Not Started'].color }} />
                    </mui.Box>
                  </mui.ListItemIcon>
                  <mui.ListItemText primary="Not Started" />
                </mui.ListItemButton>
              </mui.ListItem>
              
              <mui.ListItem disablePadding>
                <mui.ListItemButton 
                  onClick={() => handleStatusChangeFromTimeline('In Progress')}
                  sx={{ 
                    borderRadius: '6px',
                    py: 0.75,
                    '&:hover': {
                      backgroundColor: 'rgba(14, 165, 233, 0.1)'
                    }
                  }}
                >
                  <mui.ListItemIcon sx={{ minWidth: 36 }}>
                    <mui.Box 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%', 
                        bgcolor: statusColors['In Progress'].bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Icons.RunCircleOutlined fontSize="small" sx={{ color: statusColors['In Progress'].color }} />
                    </mui.Box>
                  </mui.ListItemIcon>
                  <mui.ListItemText primary="In Progress" />
                </mui.ListItemButton>
              </mui.ListItem>
              
              <mui.ListItem disablePadding>
                <mui.ListItemButton 
                  onClick={() => handleStatusChangeFromTimeline('Completed')}
                  sx={{ 
                    borderRadius: '6px',
                    py: 0.75,
                    '&:hover': {
                      backgroundColor: 'rgba(34, 197, 94, 0.1)'
                    }
                  }}
                >
                  <mui.ListItemIcon sx={{ minWidth: 36 }}>
                    <mui.Box 
                      sx={{ 
                        width: 24, 
                        height: 24, 
                        borderRadius: '50%', 
                        bgcolor: statusColors['Completed'].bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Icons.CheckCircleOutlined fontSize="small" sx={{ color: statusColors['Completed'].color }} />
                    </mui.Box>
                  </mui.ListItemIcon>
                  <mui.ListItemText primary="Completed" />
                </mui.ListItemButton>
              </mui.ListItem>
              
              {/* Add a divider and clear timeline option */}
              {selectedStatusActivity && selectedStatusActivity.START_DATE && selectedStatusActivity.END_DATE && (
                <>
                  <mui.Divider sx={{ my: 1 }} />
                  <mui.ListItem disablePadding>
                    <mui.ListItemButton 
                      onClick={() => handleEditTimeline(selectedStatusActivity)}
                      sx={{ 
                        borderRadius: '6px',
                        py: 0.75,
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.1)'
                        }
                      }}
                    >
                      <mui.ListItemIcon sx={{ minWidth: 36 }}>
                        <mui.Box 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            borderRadius: '50%', 
                            bgcolor: 'rgba(25, 118, 210, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Icons.EditCalendar fontSize="small" sx={{ color: 'primary.main' }} />
                        </mui.Box>
                      </mui.ListItemIcon>
                      <mui.ListItemText 
                        primary="Edit Timeline" 
                        secondary={`${formatDate(selectedStatusActivity.START_DATE)} - ${formatDate(selectedStatusActivity.END_DATE)}`}
                        secondaryTypographyProps={{ 
                          variant: 'caption',
                          sx: { 
                            fontSize: '0.7rem',
                            display: 'block',
                            mt: 0.25
                          }
                        }}
                      />
                    </mui.ListItemButton>
                  </mui.ListItem>
                  <mui.ListItem disablePadding>
                    <mui.ListItemButton 
                      onClick={() => handleClearDates(selectedStatusActivity.id)}
                      sx={{ 
                        borderRadius: '6px',
                        py: 0.75,
                        '&:hover': {
                          backgroundColor: 'rgba(239, 68, 68, 0.1)'
                        }
                      }}
                    >
                      <mui.ListItemIcon sx={{ minWidth: 36 }}>
                        <mui.Box 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            borderRadius: '50%', 
                            bgcolor: 'rgba(239, 68, 68, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Icons.EventBusy fontSize="small" sx={{ color: 'error.main' }} />
                        </mui.Box>
                      </mui.ListItemIcon>
                      <mui.ListItemText 
                        primary="Clear Timeline" 
                        secondary={`${formatDate(selectedStatusActivity.START_DATE)} - ${formatDate(selectedStatusActivity.END_DATE)}`}
                        secondaryTypographyProps={{ 
                          variant: 'caption',
                          sx: { 
                            fontSize: '0.7rem',
                            display: 'block',
                            mt: 0.25
                          }
                        }}
                      />
                    </mui.ListItemButton>
                  </mui.ListItem>
                </>
              )}
            </mui.List>
          </mui.Box>
        </mui.Popover>
      </ResponsiveContainer>
    </LocalizationProvider>
  );

  return (
    <Suspense fallback={<mui.CircularProgress />}>
      <IASideBar mainContent={mainContent} />
    </Suspense>
  );
};

export default PlanningDetails;
